import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { logger } from './lib/logger';
import supabase, { SUPABASE_URL, SUPABASE_ANON_KEY } from './supabase';
import { usePresence } from './useRealtimeSync';
import { useFlowchartSync } from './useFlowchartSync';
import { useUserFlowchartViewSettings, DEFAULT_END_TIME_DISPLAY } from './useUserFlowchartViewSettings';
import type { EndTimeDisplay } from './useUserFlowchartViewSettings';
import { useUndoRedoHistory } from './useUndoRedoHistory';
import { useKeyboardShortcuts } from './useKeyboardShortcuts';
import { useShareAndInvites } from './useShareAndInvites';
import { useCanvasPointerEvents } from './useCanvasPointerEvents';
import { useBoxActions } from './useBoxActions';
import { useGanttSchedule } from './useGanttSchedule';
import { useCanvasExport } from './useCanvasExport';
import PresenceLayer from './PresenceLayer';
import ProjectGanttView from './ProjectGanttView';
import { useFlowchartAccess } from './useFlowchartAccess';
import MinimapOverlay from './MinimapOverlay';
import CanvasSidebar from './CanvasSidebar';
import GanttPanel from './GanttPanel';
import CanvasTopbar from './CanvasTopbar';
import PlanSettingsDialog from './PlanSettingsDialog';
import SubitemsPanel from './SubitemsPanel';
import CanvasModals from './CanvasModals';
import CanvasBox from './CanvasBox';
import CanvasArea from './CanvasArea';
import CanvasArrow, { type CanvasArrowAction, type CanvasArrowActionArgs } from './CanvasArrow';
import CanvasOverlays from './CanvasOverlays';
import type {
  FlowchartBuilderProps,
  Box,
  BoxType,
  Arrow,
  Area,
  Department,
  Point,
  ConnectionSide,
  ConnectingState,
  ClickConnectMode,
  DraftBox,
  FlowchartData,
  SavedFlowchart,
  MigrationMap,
  SnapLine,
  DurationUnit,
  SelectionRectState,
  HistoryEntry,
  VisibleLevels,
  FlowchartWithAccess,
  Resource,
} from './types';
import type { TimelinePreset, ExportRowPreset } from './types/ganttUtils';
import { workHoursBetween, calcSubitemDuration, formatDuration, assignBoxesToAreas, calcAreaCompletion } from './lib/gantt';
import { lsGet, lsSet, lsRemove, lsGetJSON, LS_KEYS, lsGanttStartDate, lsWorkDayHours, lsHideNonWorkingHours, lsHideNonWorkingDays, lsOnlyVisibleBars, lsGanttResourceFilter, lsGanttCategoryFilter, lsGanttDurationDisplay, lsShowSubitemsInGantt, lsShowAreaGroupsInGantt, lsShowRegularBoxesInGantt, lsCanvasShowComments, lsCanvasShowSubitemBadges, lsCanvasShowUidBadges, lsCanvasShowAreas, lsFilteredExportEnabled, lsExportSelectedBoxIds } from './lib/localStorage';
import { useT } from './i18n/index';
import { getConnPt, wouldCreateCycle } from './lib/arrowRouting';

// App version
const APP_VERSION = '1.2.0';

// Version of the flowchart data structure
const CURRENT_VERSION = 2;

// Box/arrow IDs are assigned locally per browser session (nextId, a plain
// incrementing counter) rather than by any central authority. Two sessions
// creating something new around the same moment — before either has received
// the other's realtime broadcast — can independently compute the exact same
// "next" id, and the per-item merge (mergeById) can then only keep ONE of the
// two same-id items, silently dropping the other (documented as a known
// limitation in lib/collabMerge.ts; confirmed happening in production via
// change_log entries showing two different users each creating a "Box 1"
// seconds apart on the same flowchart, twice in two days).
//
// Adding this random, page-load-scoped offset every time nextId is
// (re-)established from a freshly observed max id — load(), restoreVersion(),
// but deliberately NOT the routine "bump past what a realtime update just
// showed us" adjustments, which must stay a plain max+1 or every merge would
// re-add another offset on top and IDs would balloon pointlessly — means two
// concurrent sessions' nextId counters start hundreds of millions apart, so
// anything either creates before they resync can no longer collide. IDs
// still fit comfortably in a safe JS integer (this offset is ~9 orders of
// magnitude below Number.MAX_SAFE_INTEGER).
const SESSION_ID_OFFSET = Math.floor(Math.random() * 1_000_000_000);

// Migration functions for different versions
const migrations: MigrationMap = {
  // Migration from version 1 to version 2
  1: (data): FlowchartData => {
    logger.log('Migreren van v1 naar v2...');
    const migratedBoxes = (data.boxes || []).map((box: any) => ({
      ...box,
      // Ensure all new fields are present
      type: box.type || 'regular',
      departmentColor: box.departmentColor === '#ffffff' || box.departmentColor === '#f3f4f6' ? '#dcfce7' : (box.departmentColor || '#dcfce7'),
      importantFinish: box.importantFinish || false,
      completion: box.completion !== undefined ? box.completion : 0,
      // Ensure old fields have their default values
      status: box.status || 'none',
      duration: box.duration || '',
      assignee: box.assignee || '',
      width: box.width || (box.type === 'startday' ? 45 : 180),
      height: box.height || (box.type === 'startday' ? 45 : 110)
    }));

    const migratedArrows = (data.arrows || []).map((arrow: any) => ({
      ...arrow,
      dashed: arrow.dashed !== undefined ? arrow.dashed : false
    }));

    return {
      ...data,
      boxes: migratedBoxes,
      arrows: migratedArrows,
      version: 2
    };
  },
  // Future migrations can be added here:
  // 2: (data) => { /* migration v2 to v3 */ },
};

// Main migration function that applies all necessary migrations
const migrateData = (data: Partial<FlowchartData>): FlowchartData => {
  let currentVersion = data.version || 1;
  let migratedData = { ...data } as any;

  // Check if migration is needed
  if (currentVersion >= CURRENT_VERSION) {
    return migratedData;
  }

  logger.log(`Migreren vanaf versie ${currentVersion} naar ${CURRENT_VERSION}`);

  // Apply all necessary migrations
  while (currentVersion < CURRENT_VERSION) {
    if (migrations[currentVersion]) {
      migratedData = migrations[currentVersion](migratedData);
      currentVersion++;
    } else {
      logger.error(`Geen migratie gevonden voor versie ${currentVersion}`);
      break;
    }
  }

  return migratedData as FlowchartData;
};

/**
 * Normalizes arrows from the DB into the runtime format.
 * Old records have {from, to} without fromBox/toBox/fromSide/toSide.
 * fromSide/toSide are computed from the relative box positions.
 */
function normalizeArrows(rawArrows: unknown[], boxes: Box[]): Arrow[] {
  return rawArrows.map(raw => {
    const a = raw as Record<string, unknown>;
    const fromBox = (a.fromBox ?? a.from) as number;
    const toBox   = (a.toBox   ?? a.to)   as number;

    // Sides already present — just canonicalize from/to
    if (a.fromSide && a.toSide) {
      return { ...a, fromBox, toBox } as Arrow;
    }

    // Compute sides based on box positions
    let fromSide: ConnectionSide = 'right';
    let toSide: ConnectionSide   = 'left';
    const fb = boxes.find(b => b.id === fromBox);
    const tb = boxes.find(b => b.id === toBox);
    if (fb && tb) {
      const dx = (tb.x + tb.width / 2) - (fb.x + fb.width / 2);
      const dy = (tb.y + tb.height / 2) - (fb.y + fb.height / 2);
      if (Math.abs(dx) < Math.abs(dy)) {
        fromSide = dy > 0 ? 'bottom' : 'top';
        toSide   = dy > 0 ? 'top'    : 'bottom';
      } else {
        fromSide = dx > 0 ? 'right' : 'left';
        toSide   = dx > 0 ? 'left'  : 'right';
      }
    }

    return { ...a, fromBox, toBox, fromSide, toSide } as Arrow;
  });
}

/**
 * Normalizes resources from the DB into the runtime format.
 * Old records are plain strings (before team support) without a team field.
 */
function normalizeResources(raw: unknown): Resource[] {
  const arr = Array.isArray(raw) ? raw : (typeof raw === 'string' ? (() => { try { return JSON.parse(raw || '[]'); } catch { return []; } })() : []);
  return arr.map((r: unknown) => typeof r === 'string' ? { name: r, team: '' } : r as Resource);
}

// Reusable sidebar button component



export default function FlowchartBuilder({ user, onSignOut, isActive = true, onSwitchToMindmap, onOpenProjectList }: FlowchartBuilderProps): JSX.Element {
  const { t } = useT();
  const [boxes, setBoxes] = useState<Box[]>([]);
  const [arrows, setArrows] = useState<Arrow[]>([]);
  const [dragging, setDragging] = useState<number | null>(null);
  const [connecting, setConnecting] = useState<ConnectingState | null>(null);
  const [mousePos, setMousePos] = useState<Point>({ x: 0, y: 0 });
  const [offset, setOffset] = useState<Point>({ x: 0, y: 0 });
  const [nextId, setNextId] = useState<number>(1);
  const [nextBoxNum, setNextBoxNum] = useState<number>(1);
  const [nextCalloutNum, setNextCalloutNum] = useState<number>(1);
  const [showGantt, setShowGantt] = useState<boolean>(false);
  const [showProjectGantt, setShowProjectGantt] = useState<boolean>(false);
  const [currentProjectId, setCurrentProjectId] = useState<string | null>(null);
  const [currentProjectName, setCurrentProjectName] = useState<string | null>(null);
  const [zoom, setZoom] = useState<number>(1);
  // const [saved, setSaved] = useState<SavedFlowchart[]>([]); // Replaced by ProjectList component
  const [currentId, setCurrentId] = useState<string | null>(null);
  const [title, setTitle] = useState<string>('Untitled Flowchart');
  const [defaultFlowchartId, setDefaultFlowchartId] = useState<string | null>(() => lsGet(LS_KEYS.flowchartDefaultId));
  const [hoveredBox, setHoveredBox] = useState<number | null>(null);
  const [hoveredArrow, setHoveredArrow] = useState<number | null>(null);
  const [selectedBoxes, setSelectedBoxes] = useState<number[]>([]);
  const [selectionRect, setSelectionRect] = useState<SelectionRectState | null>(null);

  const [departments, setDepartments] = useState<Department[]>([
    { name: 'Category A', color: '#dcfce7' }, // light green (default box color)
    { name: 'Category B', color: '#fce7f3' }, // light pink
    { name: 'Category C', color: '#dbeafe' }  // light blue
  ]);
  const [showLegend, setShowLegend] = useState<boolean>(false);
  const [resources, setResources] = useState<Resource[]>([]); // Team/resource entries, shared per flowchart
  const [showResources, setShowResources] = useState<boolean>(false);
  // Replaces the old simple/advanced viewMode switch (see its own removal note at
  // the DB column level — view_mode is left in place, unused, rather than dropped).
  // Per-user, like the other canvas "View" toggles (canvas_show_comments etc.) —
  // see useUserFlowchartViewSettings.ts.
  const [viewAllFields, setViewAllFields] = useState<boolean>(false);
  const [draftBox, setDraftBox] = useState<DraftBox | null>(null); // For drag-to-create functionality
  const [isEditingText, setIsEditingText] = useState<boolean>(false); // Track if user is editing text
  const [panOffset, setPanOffset] = useState<Point>({ x: 400, y: 300 }); // Canvas pan offset - start centered
  const [clipboard, setClipboard] = useState<{ boxes: Box[]; arrows: Arrow[] } | null>(null); // For copy/paste functionality
  const [lastPastedBoxes, setLastPastedBoxes] = useState<Box[] | null>(null); // Track last paste for multi-paste
const [isEditingBoxTitle, setIsEditingBoxTitle] = useState<number | null>(null); // Track which box title is being edited
  const [snapLines, setSnapLines] = useState<SnapLine[]>([]); // Snap lines for alignment guides
  const [clickConnectMode, setClickConnectMode] = useState<ClickConnectMode | null>(null); // Track first box for click-to-connect: { boxId }
  const [clickStartPos, setClickStartPos] = useState<(Point & { boxId?: number }) | null>(null); // Track initial mouse position to detect clicks vs drags
  const [durationUnit, setDurationUnit] = useState<DurationUnit>('days'); // Duration unit: hours, days, weeks
  const [endTimeDisplay, setEndTimeDisplay] = useState<EndTimeDisplay>(DEFAULT_END_TIME_DISPLAY);
  const [ganttStartDate, setGanttStartDate] = useState<Date>(new Date());
  const [ganttWorkDays, setGanttWorkDays] = useState<number[]>([1,2,3,4,5]);
  const [fullDaySchedule, setFullDaySchedule] = useState<boolean>(false);
  const [workDayStartHour, setWorkDayStartHour] = useState<number>(9);
  const [workDayEndHour, setWorkDayEndHour] = useState<number>(17);
  const workDayHoursPerDay = workDayEndHour - workDayStartHour;
  const [ganttNonWorkingDays, setGanttNonWorkingDays] = useState<string[]>([]);
  const [showPlanSettings, setShowPlanSettings] = useState<boolean>(false);
  // derived: any day excluded = workHoursOnly (scheduling logic — never changes with display toggle)
  const workHoursOnly = ganttWorkDays.length < 7;
  // display-only toggle: show/hide non-working day columns in Gantt (does NOT affect scheduling)
  const [ganttShowWorkDaysOnly, setGanttShowWorkDaysOnly] = useState<boolean>(true);
  // display-only toggle: show/hide non-working hour columns in Gantt hours mode (does NOT affect scheduling)
  const [ganttHideNonWorkingHours, setGanttHideNonWorkingHours] = useState<boolean>(true);
  // export-only toggle: drop rows with no visible bar in the "set timeline dates" range
  const [ganttOnlyVisibleBars, setGanttOnlyVisibleBars] = useState<boolean>(false);
  // display-only toggle: show subitems as their own indented rows under their parent's
  // row in the Gantt (see lib/gantt.ts's calcSubitemGanttTasks) — does NOT affect
  // scheduling, and never applies to the resource-filtered/summary views.
  const [showSubitemsInGantt, setShowSubitemsInGantt] = useState<boolean>(true);
  // display-only toggle: group boxes into Area-based "toplevel" rows in the Gantt
  // (see lib/gantt.ts's assignBoxesToAreas) — does NOT affect scheduling.
  const [showAreaGroupsInGantt, setShowAreaGroupsInGantt] = useState<boolean>(true);
  // display-only toggle: show regular box rows in the Gantt at all — turning this
  // off with showAreaGroupsInGantt on shows only the Area toplevel bars.
  const [showRegularBoxesInGantt, setShowRegularBoxesInGantt] = useState<boolean>(true);
  // display-only toggle: show a column after each task's title with its start/end
  // text (useGanttSchedule.ts's getStart/getEnd — same text as the canvas's own
  // start/end badges). Default off — opt-in, brand new setting with no prior
  // localStorage key to migrate from.
  const [showStartEndColumnInGantt, setShowStartEndColumnInGantt] = useState<boolean>(false);
  // Gantt "filter by resource" selection — per-user (see useUserFlowchartViewSettings.ts;
  // used to be localStorage-only, migrated into that table's gantt_resource_filter column)
  const [ganttResourceFilter, setGanttResourceFilter] = useState<string[]>([]);
  // Gantt "filter by category" selection — same per-user convention as ganttResourceFilter above
  const [ganttCategoryFilter, setGanttCategoryFilter] = useState<string[]>([]);
  // Gantt bar duration-label display unit — independent of the canvas's own scheduling
  // duration unit (durationUnit); only defaults to it the first time a flowchart is opened
  // (see load()), then persists whatever the user explicitly picks in the Gantt view.
  const [ganttDurationDisplay, setGanttDurationDisplay] = useState<DurationUnit>('hours');
  // "Show in Flowplan" from the Gantt view: box to select + center on once the canvas remounts
  const [pendingCenterBoxId, setPendingCenterBoxId] = useState<number | null>(null);
  useEffect(() => {
    if (showGantt || pendingCenterBoxId == null) return;
    const box = boxes.find(b => b.id === pendingCenterBoxId);
    const rect = canvasRef.current?.getBoundingClientRect();
    if (box && rect) {
      setPanOffset({
        x: rect.width / 2 - (box.x + box.width / 2) * zoom,
        y: rect.height / 2 - (box.y + box.height / 2) * zoom,
      });
    }
    setPendingCenterBoxId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showGantt, pendingCenterBoxId]);
  const [ganttVisibleLevels, setGanttVisibleLevels] = useState<VisibleLevels | null>(null);
  const [ganttLabelWidth, setGanttLabelWidth] = useState<number | null>(null);
  const [ganttTimelineView, setGanttTimelineView] = useState<{ start: string; end: string } | null>(null);
  // Named, saved timeline ranges (see types/ganttUtils.ts's TimelinePreset) — shared
  // with every collaborator via the DB column, same as the rest of the flowchart's
  // Gantt settings, no localStorage layer (matches resources/areas/departments'
  // own convention: a shared list, not a per-user preference).
  const [ganttTimelinePresets, setGanttTimelinePresets] = useState<TimelinePreset[]>([]);
  // "Filtered Export": which top-level boxes to include when exporting the Gantt as
  // PNG/SVG. Mode toggle + the currently-being-edited selection are per-user (see
  // useUserFlowchartViewSettings.ts; same convention as ganttResourceFilter above) —
  // only a selection explicitly SAVED under a name (ganttExportRowPresets) is DB-shared.
  const [ganttFilteredExportEnabled, setGanttFilteredExportEnabled] = useState<boolean>(false);
  const [ganttExportSelectedBoxIds, setGanttExportSelectedBoxIds] = useState<number[]>([]);
  const [ganttExportRowPresets, setGanttExportRowPresets] = useState<ExportRowPreset[]>([]);
  const [ganttZoom, setGanttZoom] = useState<number | null>(null);
  const [showCriticalPathCanvas, setShowCriticalPathCanvas] = useState<boolean>(false);
  
  // Share functionality state
  const [showShareDialog, setShowShareDialog] = useState<boolean>(false);
  const [showHistory, setShowHistory] = useState<boolean>(false);
  const [historyVersions, setHistoryVersions] = useState<HistoryEntry[]>([]);
  const [loadingHistory, setLoadingHistory] = useState<boolean>(false);
  const { userRole, canEdit: canEditFlowchart } = useFlowchartAccess(currentId);
  const [showHelp, setShowHelp] = useState<boolean>(false);
  const [showHelpPanel, setShowHelpPanel] = useState<boolean>(false);
  const [pendingAutosave, setPendingAutosave] = useState<any | null>(null); // unsaved work waiting for an explicit restore action
  const [lastWriteToast, setLastWriteToast] = useState<string | null>(null); // email of user who last wrote
  const [durationModeToast, setDurationModeToast] = useState<string | null>(null); // one-off notice when a box's duration source (manual vs subitems) flips
  const [syncFailed, setSyncFailed] = useState(false); // autosave to DB failed — banner + retry
  const [syncRetryTick, setSyncRetryTick] = useState(0); // bump to retrigger the autosave effect
  // syncRetryTimerRef and flowchartVersionRef have been moved to useFlowchartSync.ts
  // (along with the other sync refs) and are destructured from that hook below.
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; type: 'box' | 'canvas'; boxId?: number } | null>(null);
  const [linkPanel, setLinkPanel] = useState<{ boxId: number } | null>(null);
  const [uidPanel, setUidPanel] = useState<{ boxId: number } | null>(null);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [exportMenuY, setExportMenuY] = useState(0);
  const [editingDuration, setEditingDuration] = useState<{ id: number; value: string } | null>(null);
  // Combines the old separate "Set fixed date" (scheduledStart) and "Set earliest
  // start" (earliestStart) popups into one, with `type` choosing which field it edits.
  const [startConstraintModal, setStartConstraintModal] = useState<{ boxId: number; type: 'fixed' | 'earliest'; value: string } | null>(null);
  const [autoEndModal, setAutoEndModal] = useState<{ boxId: number; value: string } | null>(null);
  const [resizeNoteModal, setResizeNoteModal] = useState<{ boxId: number; width: number; height: number } | null>(null);
  const [copyToFlowchartModal, setCopyToFlowchartModal] = useState<{ boxIds: number[] } | null>(null);
  const [subitemsPanelBoxId, setSubitemsPanelBoxId] = useState<number | null>(null);
  const [arrowContextMenu, setArrowContextMenu] = useState<{ arrowId: number; x: number; y: number } | null>(null);
  const [ganttOrder, setGanttOrder] = useState<number[]>([]);

  // ── Area state ──────────────────────────────────────────────────────────
  const [areas, setAreas] = useState<Area[]>([]);
  const [isDrawingAreaMode, setIsDrawingAreaMode] = useState<boolean>(false);
  const [drawingArea, setDrawingArea] = useState<{ startX: number; startY: number; currentX: number; currentY: number } | null>(null);
  const [selectedAreaId, setSelectedAreaId] = useState<number | null>(null);
  const [editingAreaId, setEditingAreaId] = useState<number | null>(null);
  const [editingAreaTitle, setEditingAreaTitle] = useState<string>('');
  const [draggingArea, setDraggingArea] = useState<{ id: number; startMouseX: number; startMouseY: number; startAreaX: number; startAreaY: number } | null>(null);
  const [resizingArea, setResizingArea] = useState<{ id: number; handle: 'nw' | 'ne' | 'sw' | 'se'; startMouseX: number; startMouseY: number; startArea: { x: number; y: number; width: number; height: number } } | null>(null);
  const nextAreaIdRef = useRef<number>(1);
  const areasRef = useRef<Area[]>(areas);
  // showNotes/showSubitemBadge/showUidBadge/showAreas below are per-user (see
  // useUserFlowchartViewSettings.ts) — the canvas "View" menu's comment/badge
  // visibility toggles.
  const [showNotes, setShowNotes] = useState<boolean>(true);
  // Canvas display toggles for the subitem-count and UID badges (CanvasBox.tsx).
  const [showSubitemBadge, setShowSubitemBadge] = useState<boolean>(true);
  const [showUidBadge, setShowUidBadge] = useState<boolean>(true);
  const [showAreas, setShowAreas] = useState<boolean>(true);
  const [showStartDates, setShowStartDates] = useState<boolean>(true);
  const [showFinishDates, setShowFinishDates] = useState<boolean>(true);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  
  const linkInputRef = useRef<HTMLInputElement>(null);
  const uidInputRef = useRef<HTMLInputElement>(null);
  const canvasRef = useRef<HTMLDivElement>(null);
  const canvasContentRef = useRef<HTMLDivElement>(null); // #flowchart-canvas — written to directly during a pan drag, see useCanvasPointerEvents
  const lastLoadedAtRef = useRef<number>(0);                // timestamp of the last load() — for session timeout
  const loadRef = useRef<((f: SavedFlowchart) => void) | null>(null);  // stable ref to load() for use in event handlers
  const rightMouseDownPos = useRef<{ x: number; y: number } | null>(null); // position at right-mousedown (for pan detection)
  const isRecentTouchRef = useRef<boolean>(false); // true shortly after a touch — prevents the synthetic mousedown after touch from canceling edit mode
  const touchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null); // timer for isRecentTouch reset
  const currentIdRef  = useRef<string | null>(null);
  currentIdRef.current = currentId; // always keep synchronously up to date
  const saveRef = useRef<() => Promise<void>>(async () => {});
  const isActiveRef = useRef<boolean>(isActive);
  useEffect(() => { isActiveRef.current = isActive; }, [isActive]);
  useEffect(() => { areasRef.current = areas; }, [areas]);
  const sessionTokenRef = useRef<string | null>(null);
  const ganttOrderSaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Keep track of the auth token for the keepalive fetch in beforeunload
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }: any) => {
      sessionTokenRef.current = session?.access_token ?? null;
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event: any, session: any) => {
      sessionTokenRef.current = session?.access_token ?? null;
    });
    return () => subscription.unsubscribe();
  }, []);

  // PRESENCE — live cursors + online users. Gated on `isActive`, not just
  // `!!currentId`: FlowchartBuilder/MindmapBuilder are both always mounted (App.tsx
  // only toggles `display: none`/`block` between them, never actually unmounts
  // either), so without this, switching to mindmap mode left the flowchart's
  // presence channel subscribed forever — other users kept seeing this user as
  // "online" in the flowchart (and kept receiving their last cursor position) long
  // after they'd actually left it.
  const { onlineUsers, broadcastCursor, subscribeCursors } = usePresence(
    currentId,
    user,
    isActive && !!currentId
  );

  // REALTIME SYNC — incoming-update handling + outgoing autosave/merge, plus the
  // sync refs (lastSyncedContentRef, flowchartVersionRef, etc.) that this component's
  // load/restore/save/changelog code below reads and writes directly on the same
  // ref objects returned here. See useFlowchartSync.ts for the full scope note.
  const {
    saveNow, registerOwnWrite,
    lastSyncedContentRef, lastLocalSaveTimeRef, flowchartVersionRef,
    lastAutoLogRef, lastLoggedBoxesRef, lastLoggedArrowsRef,
  } = useFlowchartSync({
    currentId, userId: user?.id, userEmail: user?.email,
    onlineUsersCount: onlineUsers.length, syncRetryTick,
    boxes, arrows, title, departments, areas, resources, durationUnit, ganttStartDate,
    setBoxes, setArrows, setTitle, setDepartments, setAreas, setResources,
    setNextId, setGanttStartDate, setLastWriteToast, setSyncFailed, setSyncRetryTick,
    normalizeArrows, normalizeResources, diffBoxChanges, logChange,
  });

  // Per-user view/display settings (comment/badge visibility, Gantt timeline crop/
  // zoom/filters, etc.) — see useUserFlowchartViewSettings.ts's own doc comment.
  const { fetchViewSettings, updateViewSetting } = useUserFlowchartViewSettings({ userId: user?.id });

  // Meta-update for settings that remain SHARED across every collaborator (gantt_order,
  // timeline/export-row presets, scheduling columns, etc. — NOT the per-user view
  // settings above, which go through updateViewSetting instead). The realtime echo of
  // such an update also contains the boxes column (possibly stale relative to
  // un-synced local edits); without registration, that echo would be applied as a
  // remote update and revert local changes.
  const updateFlowchartMeta = React.useCallback((id: string, fields: Record<string, unknown>) => {
    return supabase.from('flowcharts').update(fields).eq('id', id).select('updated_at').single()
      .then(
        ({ data }: any) => { registerOwnWrite(data?.updated_at); },
        () => {}
      );
  }, [registerOwnWrite]);

  const { undo, redo, resetHistory, pushHistoryEntry } = useUndoRedoHistory({ boxes, arrows, setBoxes, setArrows });

  // useEffect(() => { loadAll(); }, []); // Removed - ProjectList handles loading

  // Load default flowchart on mount — ID is stored in user_profiles (cross-device)
  useEffect(() => {
    if (!user?.id) return;
    supabase.from('user_profiles').select('default_flowchart_id').eq('id', user.id).single()
      .then(({ data }) => {
        const defaultId = data?.default_flowchart_id ?? lsGet(LS_KEYS.flowchartDefaultId);
        if (!defaultId) return;
        setDefaultFlowchartId(defaultId);
        supabase.from('flowcharts').select('*').eq('id', defaultId).single().then(({ data: fc, error }) => {
          // Only load if the user hasn't already opened a flowchart of their own in the meantime
          if (!error && fc && !currentIdRef.current) {
            load(fc as SavedFlowchart);
            // The Home flowchart may belong to a project — without this, the
            // project-Gantt button in CanvasTopbar only ever appeared when opening
            // via the Open/Project List menu (which threads currentProjectId
            // through explicitly via onSetProject), never when opening via Home.
            if (fc.project_id) {
              supabase.from('projects').select('name').eq('id', fc.project_id).single().then(({ data: project }) => {
                if (currentIdRef.current === fc.id) {
                  setCurrentProjectId(fc.project_id);
                  setCurrentProjectName(project?.name ?? null);
                }
              });
            }
          }
        });
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Load tutorial_seen from user_profiles — only show the tutorial on the very first login ever
  useEffect(() => {
    if (!user?.id) return;
    supabase
      .from('user_profiles')
      .select('tutorial_seen')
      .eq('id', user.id)
      .single()
      .then(({ data }) => {
        if (data && !data.tutorial_seen) setShowHelp(true);
      });
  }, [user?.id]);

  // NOTE: there used to be a "reset content trackers on flowchart change" effect
  // here (nulling lastRemoteContentRef/lastSyncedContentRef whenever currentId
  // changed). It ran AFTER load()'s own initialization of these same refs (effects
  // fire post-commit, after the synchronous handler that calls load() has already
  // finished), so it was clobbering load()'s correct lastSyncedContentRef value
  // back to null right after every single flowchart open — the autosave effect's
  // "did anything actually change" check (lastSyncedContentRef.current ===
  // currentContent) can never be true against null, so this forced a spurious
  // "ghost" write-back of the just-loaded data on every load. load() already sets
  // lastSyncedContentRef to the correct value for the newly loaded flowchart, and
  // nothing reads lastRemoteContentRef in a way that requires it to be reset (its
  // only use, an echo-dedup check in onUpdate, degrades to a harmless no-op for
  // one message if it's briefly still holding the previous flowchart's content).

  /* loadAll function removed - replaced by ProjectList component
  const loadAll = async (): Promise<void> => {
    try {
      const { data } = await supabase.from('flowcharts').select('*');
      setSaved(data || []);
    } catch (e) {
      logger.error(e);
    }
  };
  */

const save = async (): Promise<void> => {
    // Add the version number to the data being saved
    const flowchartData: FlowchartData = { 
      version: CURRENT_VERSION,
      title, 
      boxes, 
      arrows,
      departments,
      nextId 
    };
    
    setIsSaving(true);
    try {
      let savedId = currentId;
      // What actually ends up persisted — may differ from the outer boxes/
      // arrows/etc closures above if saveNow() had to merge concurrent changes
      // from another user before writing (see saveNow's own comment: this
      // replaces an old unconditional, version-check-free write that could
      // silently wipe out someone else's just-added box or connector).
      let persisted: { title: string; boxes: Box[]; arrows: Arrow[]; departments: Department[]; areas: Area[]; resources: Resource[] } =
        { title, boxes, arrows, departments, areas, resources };

      if (currentId) {
        // Update existing - conflict-protected save (merges with concurrent
        // changes from other users instead of overwriting them)
        lastLocalSaveTimeRef.current = Date.now();
        const result = await saveNow();
        if (!result.success) throw new Error('Save conflict could not be resolved');
        persisted = result.content;
      } else {
        // Create new - use a normal Supabase insert
        const { data, error } = await supabase.from('flowcharts').insert({
          title,
          data: flowchartData,
          user_id: user.id,
          // Default the Gantt start date to today, right away, instead of leaving it
          // null — a null gantt_start_date makes every box with no scheduledStart/
          // earliestStart pin float relative to "today" on every single load, which
          // silently drifts the whole schedule forward a day at a time and stales out
          // anything that remembers an absolute date (a "Set Timeline Dates" range, a
          // saved export-row preset's own range — see CLAUDE.md's Filtered Export
          // notes). Still just a default: editable afterwards like any other Gantt
          // setting (same "Gantt start date" field in Plan Settings).
          gantt_start_date: new Date().toISOString().slice(0, 10),
          boxes: JSON.stringify(boxes),
          arrows: JSON.stringify(arrows),
          departments: JSON.stringify(departments),
          areas: JSON.stringify(areas),
          resources: JSON.stringify(resources),
        }).select().single();

        if (error) throw error;
        if (data) {
          savedId = data.id;
          setCurrentId(data.id);

          // NOTE: Owner access is now automatically created by database trigger
          // No need to manually insert into flowchart_access

          // Create default share settings
          const shareToken = Array.from(crypto.getRandomValues(new Uint8Array(16)))
            .map(b => b.toString(16).padStart(2, '0')).join('');

          const { error: shareError } = await supabase
            .from('flowchart_share_settings')
            .insert({
              flowchart_id: data.id,
              share_token: shareToken,
              link_sharing_enabled: false
            });

          if (shareError) {
            logger.error('Error creating share settings:', shareError);
          }
        }
      }

      // Save a version snapshot
      if (savedId) {
        await supabase.from('flowchart_versions').insert({
          flowchart_id: savedId,
          title: persisted.title,
          boxes: persisted.boxes as any,
          arrows: persisted.arrows as any,
          departments: persisted.departments as any,
          // Previously omitted here — a version snapshot couldn't restore resources
          // or areas even though the flowcharts table itself has always had them,
          // so a resources/areas loss (e.g. a bad collaborative merge) had no
          // history to recover from. See lib/collabMerge.ts's doc comment.
          resources: persisted.resources as any,
          areas: persisted.areas as any,
          duration_unit: durationUnit,
          saved_by: user.id,
        });
        // Keep only the latest 30 versions
        const { data: allVersions } = await supabase
          .from('flowchart_versions')
          .select('id, saved_at')
          .eq('flowchart_id', savedId)
          .order('saved_at', { ascending: false });
        if (allVersions && allVersions.length > 30) {
          const toDelete = allVersions.slice(30).map(v => v.id);
          await supabase.from('flowchart_versions').delete().in('id', toDelete);
        }
      }

      // Log the save
      if (savedId) {
        const summary = diffBoxChanges(lastLoggedBoxesRef.current, persisted.boxes, lastLoggedArrowsRef.current, persisted.arrows);
        lastLoggedBoxesRef.current = JSON.parse(JSON.stringify(persisted.boxes));
        lastLoggedArrowsRef.current = [...persisted.arrows];
        // Explicit "Save" click always gets a log entry (unlike the autosave path, which
        // skips silently) — but be honest when there's no describable content change.
        await logChange(savedId, `Saved: ${summary ?? 'Layout adjustments'}`);
      }

      // Clear autosave since data is now safely in Supabase
      lsRemove(LS_KEYS.flowchartAutosave);
    } catch (e: any) {
      alert(t('saveFailed'));
      logger.error('Save error:', e);
    } finally {
      setIsSaving(false);
    }
  };
  saveRef.current = save;

  const restoreAutosave = () => {
    if (!pendingAutosave) return;
    const data = pendingAutosave;

    let migratedBoxes: Box[] = Array.isArray(data.boxes) ? [...data.boxes] : [];
    if (!data.version || data.version === '1.0.0') {
      migratedBoxes = migratedBoxes.map(box => ({
        ...box,
        departmentColor: box.departmentColor === '#ffffff' || box.departmentColor === '#f3f4f6'
          ? '#dcfce7' : box.departmentColor,
        completion: box.completion || 0
      }));
    }
    const restoredArrows: Arrow[] = normalizeArrows(
      Array.isArray(data.arrows) ? data.arrows : [],
      migratedBoxes
    );

    setBoxes(migratedBoxes);
    setArrows(restoredArrows);
    setNextId(data.nextId || 1);
    setNextBoxNum(data.nextBoxNum || 1);
    setNextCalloutNum(data.nextCalloutNum || 1);
    if (data.departments) setDepartments(data.departments);

    // Ensure canvas is visible and viewport shows restored boxes
    setShowGantt(false);
    if (migratedBoxes.length > 0) {
      const cx = migratedBoxes.reduce((s, b) => s + b.x + b.width / 2, 0) / migratedBoxes.length;
      const cy = migratedBoxes.reduce((s, b) => s + b.y + b.height / 2, 0) / migratedBoxes.length;
      setPanOffset({ x: 500 - cx, y: 350 - cy });
      setZoom(1);
    }

    lsRemove(LS_KEYS.flowchartAutosave);
    setPendingAutosave(null);
  };

  const load = async (f: SavedFlowchart): Promise<void> => {
    // Clear autosave when loading from Supabase
    lsRemove(LS_KEYS.flowchartAutosave);
    setPendingAutosave(null);

    // Per-user view settings (comment/badge visibility, Gantt timeline crop/zoom/
    // filters, etc.) — resolved before any of the 21 states below are set, so there's
    // no race between this fetch and the rest of load() setting defaults first.
    const viewSettings = await fetchViewSettings(f.id);

    // Check if new format (boxes/arrows as separate columns) or old format (data blob)
    if (f.boxes !== undefined && f.arrows !== undefined) {
      // NEW FORMAT - direct use boxes/arrows columns
      logger.log('Loading flowchart with new format (realtime-ready)');
      const rawBoxes = Array.isArray(f.boxes) ? f.boxes : JSON.parse(f.boxes || '[]');
      const rawArrows = Array.isArray(f.arrows) ? f.arrows : JSON.parse(f.arrows || '[]');
      const loadedDepts = Array.isArray(f.departments) ? f.departments : JSON.parse(f.departments || '[{"name":"Category A","color":"#dcfce7"},{"name":"Category B","color":"#fce7f3"},{"name":"Category C","color":"#dbeafe"}]');
      const loadedResources: Resource[] = normalizeResources(f.resources);

      // One-time migration: plain numbers → explicit unit (#d / #h / #w)
      // + 'in-progress' status removed → reset to 'none'
      const legacyUnit = (f.duration_unit || 'days') === 'hours' ? 'h' : (f.duration_unit || 'days') === 'weeks' ? 'w' : 'd';
      const loadedBoxes = rawBoxes.map((b: any) => {
        let box = b.duration && /^\d+$/.test(b.duration) ? { ...b, duration: `${b.duration}${legacyUnit}` } : b;
        if (box.status === 'in-progress' || box.status === 'completed') box = { ...box, status: 'none' };
        // Normalize legacy 'title' field → 'text' (old DB records used title, new interface uses text)
        if (!box.text && (box as any).title) box = { ...box, text: (box as any).title };
        return box;
      });

      const loadedArrows = normalizeArrows(rawArrows, loadedBoxes);

      setBoxes(loadedBoxes);
      setArrows(loadedArrows);
      // Reset the undo/redo history to this flowchart's loaded state — without this the
      // previous flowchart's stack would remain, and Undo could jump back to the boxes/arrows
      // of a DIFFERENT plan (that happened to still be open in the same tab) and thereby
      // overwrite this plan with that plan's content.
      resetHistory(loadedBoxes, loadedArrows);
      setDepartments(loadedDepts);
      setResources(loadedResources);
      const loadedAreas: Area[] = f.areas
        ? (Array.isArray(f.areas) ? f.areas : JSON.parse(f.areas as string || '[]'))
        : [];
      setAreas(loadedAreas);
      nextAreaIdRef.current = loadedAreas.length > 0 ? Math.max(...loadedAreas.map(a => a.id)) + 1 : 1;

      // Calculate nextId based on highest ID in boxes and arrows
      const maxBoxId = loadedBoxes.length > 0 ? Math.max(...loadedBoxes.map((b: any) => b.id)) : 0;
      const maxArrowId = loadedArrows.length > 0 ? Math.max(...loadedArrows.map(a => a.id)) : 0;
      // + SESSION_ID_OFFSET: see its own comment — avoids id collisions with
      // whatever another concurrently-open session creates before the two sync.
      const calculatedNextId = Math.max(maxBoxId, maxArrowId, f.data?.nextId || 0) + 1 + SESSION_ID_OFFSET;
      setNextId(calculatedNextId);

      // Initialize lastSyncedContentRef so the autosave effect doesn't immediately
      // sync the just-loaded data back to the DB (prevents a ghost sync with multiple users).
      // endTimeDisplay is personal now (see useUserFlowchartViewSettings.ts) and no longer
      // part of this shared-content fingerprint — must exactly match the (also-dropped)
      // shape in useFlowchartSync.ts, or this baseline would never compare equal and would
      // trigger a spurious "Minor changes" autosave every time this flowchart is opened.
      const loadedDU = (f.duration_unit || 'days') as DurationUnit;
      lastSyncedContentRef.current = JSON.stringify({
        title: f.title, boxes: loadedBoxes, arrows: loadedArrows,
        departments: loadedDepts, areas: loadedAreas, durationUnit: loadedDU,
        resources: loadedResources
      });
      // Change-log diff baseline: without this, lastLoggedBoxesRef would stay at [] (or at the
      // previous flowchart's value) until the first save-log after opening, which would then
      // report all boxes as "Added" instead of the actual edit. lastAutoLogRef is set to 0 so
      // that the very next edit logs immediately (with this correct baseline) instead of
      // waiting up to 5 minutes.
      lastLoggedBoxesRef.current = loadedBoxes;
      lastLoggedArrowsRef.current = loadedArrows;
      lastAutoLogRef.current = 0;
      // Otherwise a carried-over timestamp from actively editing the PREVIOUS
      // flowchart (wall-clock time, unrelated to this one) can make onUpdate's
      // timestamp-guard wrongly reject a genuinely fresh update for this
      // newly-opened flowchart as "older than our last local save".
      lastLocalSaveTimeRef.current = 0;

      // Restore ganttStartDate: the DB takes priority, localStorage as fallback.
      // If there's no stored date, use today as the default and save it
      // immediately so it doesn't jump back to today again on the next open.
      const dbGanttDate = f.gantt_start_date ? new Date(f.gantt_start_date) : null;
      const storedGanttDate = lsGet(lsGanttStartDate(f.id));
      const localGanttDate = storedGanttDate ? new Date(storedGanttDate) : null;
      const hasPersistedDate = (dbGanttDate && !isNaN(dbGanttDate.getTime())) || (localGanttDate && !isNaN(localGanttDate.getTime()));
      const resolvedGanttDate = (dbGanttDate && !isNaN(dbGanttDate.getTime())) ? dbGanttDate
        : (localGanttDate && !isNaN(localGanttDate.getTime())) ? localGanttDate
        : new Date();
      setGanttStartDate(resolvedGanttDate);
      if (!hasPersistedDate) {
        // No stored date found — save the current date so it doesn't reset every day
        const isoValue = resolvedGanttDate.toISOString().slice(0, 10);
        lsSet(lsGanttStartDate(f.id), isoValue);
        updateFlowchartMeta(f.id, { gantt_start_date: isoValue });
      }

      // Load non-working days, full_day_schedule, work_days and visible levels from DB
      setGanttNonWorkingDays(Array.isArray(f.non_working_days) ? f.non_working_days : []);
      setFullDaySchedule(f.full_day_schedule === true);
      setGanttWorkDays(Array.isArray(f.work_days) && f.work_days.length > 0 ? f.work_days : [1,2,3,4,5]);
      // Workday start/end hour: DB columns first, then localStorage fallback (DB columns
      // may not exist yet on older schemas — a missing column just reads back undefined).
      {
        const storedWorkDayHours = lsGetJSON<{ start: number; end: number } | null>(lsWorkDayHours(f.id), null);
        setWorkDayStartHour(typeof f.work_day_start_hour === 'number' ? f.work_day_start_hour : storedWorkDayHours?.start ?? 9);
        setWorkDayEndHour(typeof f.work_day_end_hour === 'number' ? f.work_day_end_hour : storedWorkDayHours?.end ?? 17);
      }
      // Per-user view settings below: viewSettings (this user's own row) first, then the
      // UNCHANGED localStorage key as a fallback (e.g. a user backfilled with a null value
      // because the flowchart's old shared column was itself never set), then a hardcoded
      // default. The old flowcharts.gantt_*/canvas_* shared columns are deliberately NOT
      // consulted anymore — nothing writes to them after this migration, so they're frozen,
      // unmaintained data, not a "current" setting worth falling back to.
      {
        const storedHideNonWorkingHours = lsGetJSON<boolean | null>(lsHideNonWorkingHours(f.id), null);
        setGanttHideNonWorkingHours(viewSettings?.ganttHideNonWorkingHours ?? storedHideNonWorkingHours ?? true);
      }
      {
        const storedHideNonWorkingDays = lsGetJSON<boolean | null>(lsHideNonWorkingDays(f.id), null);
        setGanttShowWorkDaysOnly(viewSettings?.ganttHideNonWorkingDays ?? storedHideNonWorkingDays ?? true);
      }
      {
        const storedOnlyVisibleBars = lsGetJSON<boolean | null>(lsOnlyVisibleBars(f.id), null);
        setGanttOnlyVisibleBars(viewSettings?.ganttOnlyVisibleBars ?? storedOnlyVisibleBars ?? false);
      }
      {
        const storedShowSubitems = lsGetJSON<boolean | null>(lsShowSubitemsInGantt(f.id), null);
        setShowSubitemsInGantt(viewSettings?.ganttShowSubitems ?? storedShowSubitems ?? true);
      }
      {
        const storedShowAreas = lsGetJSON<boolean | null>(lsShowAreaGroupsInGantt(f.id), null);
        setShowAreaGroupsInGantt(viewSettings?.ganttShowAreas ?? storedShowAreas ?? true);
      }
      {
        const storedShowRegularBoxes = lsGetJSON<boolean | null>(lsShowRegularBoxesInGantt(f.id), null);
        setShowRegularBoxesInGantt(viewSettings?.ganttShowRegularBoxes ?? storedShowRegularBoxes ?? true);
      }
      setShowStartEndColumnInGantt(viewSettings?.ganttShowStartEndColumn ?? false);
      // Resource/category filter + "Filtered Export" mode/selection: viewSettings already
      // resolved these (including the one-time localStorage->DB migrate-up) — see
      // useUserFlowchartViewSettings.ts's fetchViewSettings.
      setGanttResourceFilter(viewSettings?.ganttResourceFilter ?? []);
      setGanttCategoryFilter(viewSettings?.ganttCategoryFilter ?? []);
      setGanttFilteredExportEnabled(viewSettings?.ganttFilteredExportEnabled ?? false);
      setGanttExportSelectedBoxIds(viewSettings?.ganttExportSelectedBoxIds ?? []);
      // Named, saved export row selections (types/ganttUtils.ts's ExportRowPreset) — DB
      // column, shared with every collaborator, same convention as ganttTimelinePresets.
      setGanttExportRowPresets(Array.isArray(f.gantt_export_row_presets) ? f.gantt_export_row_presets as ExportRowPreset[] : []);
      // Gantt duration-label display unit — only defaults to the canvas's own (shared)
      // duration unit (loadedDU) if neither viewSettings nor localStorage has one yet;
      // once explicitly chosen, it persists independently of later canvas duration-unit changes.
      {
        const storedDurationDisplay = lsGetJSON<DurationUnit | null>(lsGanttDurationDisplay(f.id), null);
        setGanttDurationDisplay(viewSettings?.ganttDurationDisplay ?? storedDurationDisplay ?? loadedDU);
      }
      setGanttVisibleLevels(viewSettings?.ganttVisibleLevels ?? null);
      setGanttLabelWidth(viewSettings?.ganttLabelWidth ?? null);
      setGanttTimelineView(viewSettings?.ganttTimelineView ?? null);
      setGanttTimelinePresets(Array.isArray(f.gantt_timeline_presets) ? f.gantt_timeline_presets as TimelinePreset[] : []);
      setGanttZoom(viewSettings?.ganttZoom ?? null);

      {
        const storedShowComments = lsGetJSON<boolean | null>(lsCanvasShowComments(f.id), null);
        setShowNotes(viewSettings?.canvasShowComments ?? storedShowComments ?? true);
      }
      {
        const storedShowSubitemBadges = lsGetJSON<boolean | null>(lsCanvasShowSubitemBadges(f.id), null);
        setShowSubitemBadge(viewSettings?.canvasShowSubitemBadges ?? storedShowSubitemBadges ?? true);
      }
      {
        const storedShowUidBadges = lsGetJSON<boolean | null>(lsCanvasShowUidBadges(f.id), null);
        setShowUidBadge(viewSettings?.canvasShowUidBadges ?? storedShowUidBadges ?? true);
      }
      {
        const storedCanvasShowAreas = lsGetJSON<boolean | null>(lsCanvasShowAreas(f.id), null);
        setShowAreas(viewSettings?.canvasShowAreas ?? storedCanvasShowAreas ?? true);
      }
      setViewAllFields(viewSettings?.canvasViewAllFields ?? false);

      logger.log(`Calculated nextId: ${calculatedNextId} (maxBox: ${maxBoxId}, maxArrow: ${maxArrowId})`);
    } else if (f.data) {
      // OLD FORMAT - migrate from data blob
      logger.log('Loading flowchart with old format - migrating...');
      const migratedData = migrateData(f.data);

      // Show a notice if the data was migrated
      if (migratedData.version > (f.data.version || 1)) {
        logger.log(`Flowchart is bijgewerkt van v${f.data.version || 1} naar v${migratedData.version}`);
      }

      const rawBoxes2 = migratedData.boxes || [];
      const legacyUnit2 = (f.duration_unit || 'days') === 'hours' ? 'h' : (f.duration_unit || 'days') === 'weeks' ? 'w' : 'd';
      const loadedBoxes = rawBoxes2.map(b => {
        let box = b.duration && /^\d+$/.test(b.duration) ? { ...b, duration: `${b.duration}${legacyUnit2}` } : b;
        if (!box.text && (box as any).title) box = { ...box, text: (box as any).title };
        return box;
      });
      const loadedArrows = normalizeArrows(migratedData.arrows || [], loadedBoxes);
      const defaultDepts = [
        { name: 'Category A', color: '#dcfce7' },
        { name: 'Category B', color: '#fce7f3' },
        { name: 'Category C', color: '#dbeafe' }
      ];
      setBoxes(loadedBoxes);
      setArrows(loadedArrows);
      // Undo/redo history reset — see the matching comment in the NEW FORMAT branch above.
      resetHistory(loadedBoxes, loadedArrows);
      // + SESSION_ID_OFFSET: see its own comment.
      setNextId((migratedData.nextId || 1) + SESSION_ID_OFFSET);
      setDepartments(defaultDepts);
      const loadedResources: Resource[] = normalizeResources(f.resources);
      setResources(loadedResources);

      const loadedDU = (f.duration_unit || 'days') as DurationUnit;
      // Shape must match the autosave effect's currentContent (durationUnit, not
      // endTimeDisplay — personal now, see the matching comment in the NEW FORMAT
      // branch above) — a shape mismatch here causes spurious writes.
      lastSyncedContentRef.current = JSON.stringify({
        title: f.title, boxes: loadedBoxes, arrows: loadedArrows,
        departments: defaultDepts, areas: [], durationUnit: loadedDU,
        resources: loadedResources
      });
      // Change-log diff baseline — see the matching comment in the NEW FORMAT branch above.
      lastLoggedBoxesRef.current = loadedBoxes;
      lastLoggedArrowsRef.current = loadedArrows;
      lastAutoLogRef.current = 0;
      // Otherwise a carried-over timestamp from actively editing the PREVIOUS
      // flowchart (wall-clock time, unrelated to this one) can make onUpdate's
      // timestamp-guard wrongly reject a genuinely fresh update for this
      // newly-opened flowchart as "older than our last local save".
      lastLocalSaveTimeRef.current = 0;
    }

    setTitle(f.title);
    setCurrentId(f.id);
    // durationUnit (shared) and endTimeDisplay (personal) are deliberately set
    // independently now — see the decoupling note on onEndTimeDisplayChange/
    // onDurationUnitChange below for why neither forces the other anymore.
    if (f.duration_unit) setDurationUnit(f.duration_unit as DurationUnit);
    setEndTimeDisplay(viewSettings?.endTimeDisplay ?? DEFAULT_END_TIME_DISPLAY);
    setGanttOrder(Array.isArray(f.gantt_order) ? f.gantt_order : []);
    flowchartVersionRef.current = typeof f.version === 'number' ? f.version : 0;
    lastLoadedAtRef.current = Date.now();
  };
  loadRef.current = load; // always keep up to date for use in event handlers

  useShareAndInvites({ user, load, setCurrentId, setTitle });

  useEffect(() => {
    if (!currentId || ganttOrder.length === 0) return;
    if (ganttOrderSaveTimeoutRef.current) clearTimeout(ganttOrderSaveTimeoutRef.current);
    ganttOrderSaveTimeoutRef.current = setTimeout(async () => {
      await updateFlowchartMeta(currentId, { gantt_order: ganttOrder });
    }, 600);
  }, [ganttOrder, currentId, updateFlowchartMeta]);

  const loadChangeLog = async () => {
    if (!currentId) return;
    setLoadingHistory(true);
    const { data } = await supabase
      .from('change_log')
      .select('user_email, action, created_at')
      .eq('flowchart_id', currentId)
      .order('created_at', { ascending: false })
      .limit(100);
    setHistoryVersions(data || []);
    setLoadingHistory(false);
  };

  // Hoisted function declarations (not const arrow fns): useFlowchartSync is called
  // earlier in this component and needs to pass these in by reference.
  //
  // Returns null when nothing worth mentioning changed — in particular, when boxes
  // only moved/resized (x/y/width/height), which happens constantly from ordinary
  // dragging and isn't a content edit. Callers should skip logging a changelog
  // entry entirely in that case rather than writing a vague "Minor changes" line.
  function diffBoxChanges(prev: Box[], curr: Box[], prevArrows: Arrow[], currArrows: Arrow[]): string | null {
    const prevMap = new Map(prev.map(b => [b.id, b]));
    const currMap = new Map(curr.map(b => [b.id, b]));
    const label = (b: Box) => b.text || '';
    const added = curr.filter(b => !prevMap.has(b.id));
    const removed = prev.filter(b => !currMap.has(b.id));

    // Named by the boxes they connect (falling back to whichever side's box still
    // exists, e.g. an arrow removed as a side-effect of deleting one of its boxes)
    // rather than just an updated total — "170 connections" reads as if most/all
    // of them changed, when it's usually one or two.
    const prevArrowMap = new Map(prevArrows.map(a => [a.id, a]));
    const currArrowMap = new Map(currArrows.map(a => [a.id, a]));
    const addedArrows = currArrows.filter(a => !prevArrowMap.has(a.id));
    const removedArrows = prevArrows.filter(a => !currArrowMap.has(a.id));
    const arrowLabel = (a: Arrow) => {
      const from = currMap.get(a.fromBox) ?? prevMap.get(a.fromBox);
      const to = currMap.get(a.toBox) ?? prevMap.get(a.toBox);
      return `"${from?.text || '?'}" → "${to?.text || '?'}"`;
    };

    type Change = { box: Box; from: string; to: string };
    const renamed: Change[] = [];
    const durationChanged: Change[] = [];
    const statusChanged: Change[] = [];
    const assigneeChanged: Change[] = [];
    const startChanged: Change[] = [];
    const propsChanged: Box[] = [];

    curr.forEach(b => {
      const p = prevMap.get(b.id);
      if (!p) return;
      if (label(p) !== label(b) && label(b)) renamed.push({ box: b, from: label(p) || '(empty)', to: label(b) });
      if ((p.duration || '') !== (b.duration || '')) durationChanged.push({ box: b, from: p.duration || 'none', to: b.duration || 'none' });
      if ((p.status || 'none') !== (b.status || 'none')) statusChanged.push({ box: b, from: p.status || 'none', to: b.status || 'none' });
      if ((p.assignee || '') !== (b.assignee || '')) assigneeChanged.push({ box: b, from: p.assignee || 'unassigned', to: b.assignee || 'unassigned' });
      if ((p.scheduledStart || '') !== (b.scheduledStart || '') || (p.earliestStart || '') !== (b.earliestStart || '')) {
        startChanged.push({ box: b, from: p.scheduledStart || p.earliestStart || 'none', to: b.scheduledStart || b.earliestStart || 'none' });
      }
      if (
        !!p.alwaysOn !== !!b.alwaysOn || !!p.is24_5 !== !!b.is24_5 || !!p.importantFinish !== !!b.importantFinish ||
        (p.completion || 0) !== (b.completion || 0) || (p.departmentColor || '') !== (b.departmentColor || '') ||
        (p.url || '') !== (b.url || '') || JSON.stringify(p.resourceHours || {}) !== JSON.stringify(b.resourceHours || {})
      ) propsChanged.push(b);
      // x/y/width/height differing without any of the above is a pure move/resize —
      // deliberately not tracked into any bucket, so it contributes nothing to `parts`.
    });

    const parts: string[] = [];
    const describe = (list: Box[], tag: string, max = 3) => {
      if (list.length === 0) return;
      const names = list.slice(0, max).map(b => `"${label(b) || '…'}"`).join(', ');
      parts.push(`${tag}: ${names}${list.length > max ? ` +${list.length - max}` : ''}`);
    };
    // Renamed shows old → new text directly (that IS the identifying info, no
    // separate label needed); the others keep the box's current text as the label
    // and show the changed field's old → new value alongside it.
    const describeRename = (list: Change[], max = 2) => {
      if (list.length === 0) return;
      const items = list.slice(0, max).map(({ from, to }) => `"${from}" → "${to}"`).join(', ');
      parts.push(`Renamed: ${items}${list.length > max ? ` +${list.length - max}` : ''}`);
    };
    const describeChange = (list: Change[], tag: string, max = 3) => {
      if (list.length === 0) return;
      const items = list.slice(0, max).map(({ box, from, to }) => `"${label(box) || '…'}" (${from} → ${to})`).join(', ');
      parts.push(`${tag}: ${items}${list.length > max ? ` +${list.length - max}` : ''}`);
    };
    describe(added, 'Added');
    describe(removed, 'Deleted');
    describeRename(renamed);
    describeChange(durationChanged, 'Duration changed');
    describeChange(statusChanged, 'Status changed');
    describeChange(assigneeChanged, 'Assignee changed');
    describeChange(startChanged, 'Start date changed');
    describe(propsChanged, 'Properties changed');
    const describeArrows = (list: Arrow[], tag: string, max = 3) => {
      if (list.length === 0) return;
      const items = list.slice(0, max).map(arrowLabel).join(', ');
      parts.push(`${tag}: ${items}${list.length > max ? ` +${list.length - max}` : ''}`);
    };
    describeArrows(addedArrows, 'Connections added');
    describeArrows(removedArrows, 'Connections removed');
    return parts.length > 0 ? parts.join('; ') : null;
  }

  async function logChange(flowchartId: string, action: string) {
    try {
      await supabase.from('change_log').insert({
        flowchart_id: flowchartId,
        user_id: user.id,
        user_email: user.email || null,
        action,
      });
    } catch (_) {}
  }

  const handleRestoreVersion = async (versionData: SavedFlowchart, flowchart: FlowchartWithAccess) => {
    const restoredBoxes: Box[] = Array.isArray(versionData.boxes) ? versionData.boxes as Box[] : JSON.parse((versionData.boxes as string) || '[]');
    const restoredArrows: Arrow[] = normalizeArrows(
      Array.isArray(versionData.arrows) ? versionData.arrows : JSON.parse((versionData.arrows as string) || '[]'),
      restoredBoxes
    );
    const restoredDepts: Department[] = Array.isArray(versionData.departments) ? versionData.departments as Department[] : JSON.parse((versionData.departments as string) || '[]');
    // Older version snapshots (saved before resources/areas were added to
    // flowchart_versions) have these as null — treat that as "not captured by
    // this snapshot" and leave the current resources/areas alone, rather than
    // wiping them. A snapshot that DID capture them (even as an empty array)
    // is restored normally.
    const restoredResources: Resource[] | undefined = versionData.resources != null ? normalizeResources(versionData.resources) : undefined;
    const restoredAreas: Area[] | undefined = versionData.areas != null
      ? (Array.isArray(versionData.areas) ? versionData.areas as Area[] : JSON.parse((versionData.areas as string) || '[]'))
      : undefined;
    setTitle(versionData.title || flowchart.title || 'Untitled Flowchart');
    setBoxes(restoredBoxes);
    setArrows(restoredArrows);
    // Undo/redo history reset — see the matching comment in load(). Without this, Undo
    // after a version restore could jump back to the state before the restore, or (if
    // this is a DIFFERENT plan than the one previously open) to that other plan's content.
    resetHistory(restoredBoxes, restoredArrows);
    setDepartments(restoredDepts);
    if (restoredResources !== undefined) setResources(restoredResources);
    if (restoredAreas !== undefined) {
      setAreas(restoredAreas);
      nextAreaIdRef.current = restoredAreas.length > 0 ? Math.max(...restoredAreas.map(a => a.id)) + 1 : 1;
    }
    if (versionData.duration_unit) setDurationUnit(versionData.duration_unit as DurationUnit);
    const maxId = restoredBoxes.reduce((m, b) => Math.max(m, b.id), 0);
    // + SESSION_ID_OFFSET: see its own comment.
    setNextId(maxId + 1 + SESSION_ID_OFFSET);
    setCurrentId(flowchart.id);
    // Use a direct Supabase update with flowchart.id instead of saveNow(), because
    // saveNow() uses the flowchartId from the hook closure (= currentId from the
    // previous render). If you restore a different flowchart than the currently
    // open one, it would otherwise overwrite the wrong flowchart. Also, unlike
    // saveNow(), restoring a version is an intentional "replace everything with
    // this snapshot" action, so an unconditional overwrite here is correct —
    // this one case doesn't want a merge with concurrent changes.
    const updatePayload: Record<string, unknown> = {
      title: versionData.title || flowchart.title || 'Untitled Flowchart',
      boxes: JSON.stringify(restoredBoxes),
      arrows: JSON.stringify(restoredArrows),
      departments: JSON.stringify(restoredDepts),
      duration_unit: versionData.duration_unit || durationUnit,
      updated_at: new Date().toISOString(),
    };
    // Only include resources/areas in the write when this snapshot actually
    // captured them — omitting the key leaves the column untouched in Postgres,
    // whereas including it as [] would wipe a populated current value.
    if (restoredResources !== undefined) updatePayload.resources = restoredResources;
    if (restoredAreas !== undefined) updatePayload.areas = JSON.stringify(restoredAreas);
    const { data: restoredRow } = await supabase.from('flowcharts').update(updatePayload).eq('id', flowchart.id).select('updated_at, version').single();
    registerOwnWrite(restoredRow?.updated_at);
    if (typeof restoredRow?.version === 'number') flowchartVersionRef.current = restoredRow.version;
    // Without this, every subsequent merge (onUpdate's realtime handler, the
    // autosave conflict-retry path) computes `base` from the PRE-restore
    // content — a stale baseline that makes genuinely-unchanged-since-restore
    // items look either "changed" or "deleted" at random, depending on what the
    // old content happened to contain. Same shape load() uses for this same ref.
    lastSyncedContentRef.current = JSON.stringify({
      title: versionData.title || flowchart.title || 'Untitled Flowchart',
      boxes: restoredBoxes, arrows: restoredArrows,
      departments: restoredDepts, areas: restoredAreas ?? areas,
      durationUnit: (versionData.duration_unit || durationUnit) as DurationUnit,
      resources: restoredResources ?? resources,
    });
    await logChange(flowchart.id, `Version restored from ${new Date(versionData.saved_at ?? Date.now()).toLocaleString()}`);
  };

  const newFlow = (skipConfirm = false): void => {
    if (!skipConfirm && (boxes.length || arrows.length) && !window.confirm(t('confirmNewFlowchart'))) return;
    setBoxes([]);
    setArrows([]);
    setNextId(1);
    setNextBoxNum(1);
    setNextCalloutNum(1);
    setTitle('Untitled Flowchart');
    setCurrentId(null);
    setViewAllFields(false);
    setGanttNonWorkingDays([]);
    setGanttVisibleLevels(null);
    setGanttLabelWidth(null);
    setGanttTimelineView(null);
    setGanttTimelinePresets([]);
    setGanttFilteredExportEnabled(false);
    setGanttExportSelectedBoxIds([]);
    setGanttExportRowPresets([]);
    setGanttZoom(null);
    setGanttResourceFilter([]);

    // Reset departments to default
    setDepartments([
      { name: 'Category A', color: '#dcfce7' },
      { name: 'Category B', color: '#fce7f3' },
      { name: 'Category C', color: '#dbeafe' }
    ]);
    setResources([]);
    setAreas([]);
    nextAreaIdRef.current = 1;
  };

  const del = async (id: string): Promise<void> => {
    if (!window.confirm(t('confirmTrashFlowchart'))) return;
    try {
      const { error } = await supabase
        .from('flowcharts')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', id);
      if (error) throw error;
      if (currentId === id) newFlow(true);
    } catch (e: any) {
      alert(t('deleteFailed'));
    }
  };

  /**
   * Starts drag-to-create flow for a new box
   * @param boxType - Type of box to create ('regular', 'callout', etc.)
   */
  const startDragToCreate = (boxType: BoxType): void => {
    const boxW = 180, boxH = 110;
    // Calculate the center of the visible canvas area in canvas coordinates
    const rect = canvasRef.current?.getBoundingClientRect();
    const centerX = rect ? (rect.width  / 2 - panOffset.x) / zoom - boxW / 2 : 0;
    const centerY = rect ? (rect.height / 2 - panOffset.y) / zoom - boxH / 2 : 0;
    const defaultText = boxType === 'callout' ? 'Status:' : `Box ${nextBoxNum}`;
    const newBox: Partial<Box> = {
      id: nextId,
      title: defaultText,
      text: defaultText,
      duration: '',
      assignee: '',
      status: 'none',
      type: boxType,
      x: centerX,
      y: centerY,
      width: boxW,
      height: boxH,
      importantFinish: false,
      departmentColor: boxType === 'callout' ? undefined : '#dcfce7',
      completion: 0
    };
    setDraftBox(newBox as DraftBox);
  };

  /**
   * Creates a new regular box via drag-to-create
   */
  const addBox = (): void => {
    if (!canEditFlowchart) {
      alert(t('needEditorAccessBoxes'));
      return;
    }
    startDragToCreate('regular');
  };

  
  /**
   * Creates a new callout/note via drag-to-create
   */
  const addCallout = (): void => {
    if (!canEditFlowchart) {
      alert(t('needEditorAccessNotes'));
      return;
    }
    startDragToCreate('callout');
  };

  /**
   * Adds a start day marker to the canvas
   */
  const addStartDay = (): void => {
    if (!canEditFlowchart) {
      alert(t('needEditorAccessStartDays'));
      return;
    }
    setBoxes([...boxes, {
      id: nextId,
      title: '0', // Start day number
      text: '0',
      duration: '',
      assignee: '',
      status: 'none',
      type: 'startday',
      x: 100 + (nextId * 30) % 400,
      y: 100 + (nextId * 30) % 300,
      width: 45,
      height: 45,
      departmentColor: '#dcfce7',
      completion: 0,
      importantFinish: false
    }]);
    setNextId(nextId + 1);
  };

  const handleToggleHome = async () => {
    if (!currentId || !user?.id) return;
    if (currentId === defaultFlowchartId) {
      setDefaultFlowchartId(null);
      lsRemove(LS_KEYS.flowchartDefaultId);
      lsRemove(LS_KEYS.homeMapType);
      const { error } = await supabase.from('user_profiles').update({ default_flowchart_id: null, home_map_type: null }).eq('id', user.id);
      if (error) { logger.error('Home flowchart clear error:', error); alert(t('saveHomeFailed')); }
    } else {
      setDefaultFlowchartId(currentId);
      lsSet(LS_KEYS.flowchartDefaultId, currentId);
      lsRemove(LS_KEYS.mindmapDefaultId);
      lsSet(LS_KEYS.homeMapType, 'flowchart');
      const { error } = await supabase.from('user_profiles').update({ default_flowchart_id: currentId, default_mindmap_id: null, home_map_type: 'flowchart' }).eq('id', user.id);
      if (error) { logger.error('Home flowchart save error:', error); alert(t('saveHomeFailed')); }
    }
  };

  // getIcon (visual icon for box.status) moved into CanvasBox.tsx as getStatusIcon —
  // see the comment there for why computing it here and passing it as a prop
  // defeated CanvasBox's memo on every render.

  // Handle click-to-connect mode
  const handleBoxClickForConnect = React.useCallback((boxId: number) => {
    if (clickConnectMode === null || !clickConnectMode.boxId) {
      // First click - select source box
      setClickConnectMode({ boxId });
    } else if (clickConnectMode.boxId !== boxId) {
      // Second click - create connection
      const fromBox = boxes.find(b => b.id === clickConnectMode.boxId);
      const toBox = boxes.find(b => b.id === boxId);
      
      if (fromBox && toBox) {
        // Calculate best connection points
        const fromCenterX = fromBox.x + fromBox.width / 2;
        const fromCenterY = fromBox.y + fromBox.height / 2;
        const toCenterX = toBox.x + toBox.width / 2;
        const toCenterY = toBox.y + toBox.height / 2;
        
        const dx = toCenterX - fromCenterX;
        const dy = toCenterY - fromCenterY;
        const angle = Math.atan2(dy, dx) * 180 / Math.PI;
        const normalizedAngle = (angle + 360) % 360;
        
        let fromSide: ConnectionSide, toSide: ConnectionSide;

        if (normalizedAngle >= 45 && normalizedAngle < 135) {
          fromSide = 'bottom';
          toSide = 'top';
        } else if (normalizedAngle >= 135 && normalizedAngle < 225) {
          fromSide = 'left';
          toSide = 'right';
        } else if (normalizedAngle >= 225 && normalizedAngle < 315) {
          fromSide = 'top';
          toSide = 'bottom';
        } else {
          fromSide = 'right';
          toSide = 'left';
        }
        
        const isDashed = fromBox.type === 'callout' || toBox.type === 'callout';

        if (wouldCreateCycle(clickConnectMode.boxId, boxId, arrows)) {
          alert(t('circularReference'));
          setClickConnectMode({});
          return;
        }

        setArrows([...arrows, {
          fromBox: clickConnectMode.boxId,
          fromSide,
          toBox: boxId,
          toSide,
          id: nextId,  // Use nextId instead of Date.now()
          dashed: isDashed
        }]);
        
        setNextId(nextId + 1);  // Increment for next arrow
      }
      
      // Keep Quick Connect mode active - just reset the selected box
      setClickConnectMode({});
    } else {
      // Clicked same box - deselect it but keep mode active
      setClickConnectMode({});
    }
  }, [clickConnectMode, boxes, arrows, nextId, t]);

  // "Make private comment" (Box.privateTo — see its own type comment): a box private
  // to another user is invisible to everyone else. visibleBoxes/visibleArrows sit
  // between the raw, fully-synced `boxes`/`arrows` state (never filtered — collab
  // merge and save must never discard someone else's private comment) and every
  // rendering/scheduling/export-facing consumer below, so privacy only has to be
  // handled in exactly one place rather than in every one of those consumers
  // independently (same reasoning as topLevelBoxes/topLevelArrows's own subitem
  // filtering, which composes with this).
  const visibleBoxes = useMemo(
    () => boxes.filter(b => !b.privateTo || b.privateTo === user?.id),
    [boxes, user?.id],
  );
  const visibleBoxIds = useMemo(() => new Set(visibleBoxes.map(b => b.id)), [visibleBoxes]);
  const visibleArrows = useMemo(
    () => arrows.filter(a => visibleBoxIds.has(a.fromBox) && visibleBoxIds.has(a.toBox)),
    [arrows, visibleBoxIds],
  );

  // Subitems (see Box.parentBoxId's own comment) are edited via their own popup and
  // never shown on the main canvas — topLevelBoxes/subitemCountByBoxId feed every
  // main-canvas-facing consumer below (box rendering, minimap, marquee-select
  // hit-testing, snap targets) so each doesn't need its own parentBoxId filter.
  const topLevelBoxes = useMemo(() => visibleBoxes.filter(b => b.parentBoxId === undefined), [visibleBoxes]);
  const subitemCountByBoxId = useMemo(() => {
    const map = new Map<number, number>();
    for (const b of boxes) {
      if (b.parentBoxId !== undefined) map.set(b.parentBoxId, (map.get(b.parentBoxId) || 0) + 1);
    }
    return map;
  }, [boxes]);
  // Same top-level-only filtering for export (SVG/PNG/XLSX, see useCanvasExport
  // below) — a subitem is edited via its own popup and should never show up as its
  // own row/box in an export any more than it does on the main canvas.
  const topLevelArrows = useMemo(() => {
    const ids = new Set(topLevelBoxes.map(b => b.id));
    return visibleArrows.filter(a => ids.has(a.fromBox) && ids.has(a.toBox));
  }, [visibleArrows, topLevelBoxes]);

  const { handleDown, applyZoom, startPanning, isPanningRef } = useCanvasPointerEvents({
    boxes, arrows, canvasBoxes: topLevelBoxes, setBoxes, selectedBoxes, setSelectedBoxes,
    setNextId, setNextBoxNum, setNextCalloutNum, pushHistoryEntry,
    dragging, setDragging, offset, setOffset, clickStartPos, setClickStartPos, setSnapLines,
    canvasRef, canvasContentRef, zoom, setZoom, panOffset, setPanOffset,
    selectionRect, setSelectionRect, setMousePos,
    draftBox, setDraftBox,
    showNotes, onPlaceCalloutHidden: () => alert(t('commentHiddenInViewWarning')),
    isEditingBoxTitle, setIsEditingBoxTitle, setIsEditingText, clickConnectMode, setContextMenu, setShowHistory, handleBoxClickForConnect,
    setAreas, drawingArea, setDrawingArea, setIsDrawingAreaMode, setSelectedAreaId,
    draggingArea, setDraggingArea, resizingArea, setResizingArea, nextAreaIdRef,
    isRecentTouchRef, touchTimerRef,
  });

  /**
   * Deletes an arrow connection
   * @param id - Arrow ID to delete
   */
  const delArr = (id: number): void => {
    if (!canEditFlowchart) {
      alert(t('needEditorAccessDeleteConnections'));
      return;
    }
    setArrows(arrows.filter(a => a.id !== id));
  };

  // Arrow interactions: FlowchartBuilder re-renders on every pan/select frame (see
  // handleMove and CanvasArrow's own doc comment), and CanvasArrow used to be passed
  // a fresh closure for each of onHoverEnter/onHoverLeave/onDelete/onShowContextMenu
  // on every one of those renders — enough on its own to defeat React.memo for all
  // ~150+ arrows even when nothing about a given arrow changed. Routing all four
  // through one referentially-stable dispatcher fixes that; arrowActionRef always
  // points at the latest real implementation (reassigned every render below), so
  // there's no stale-closure risk despite the wrapper never changing identity.
  const arrowActionRef = useRef<(action: CanvasArrowAction, arrow: Arrow, ...args: any[]) => void>(() => {});
  // One named handler per action, each checked against its OWN entry in
  // CanvasArrowActionArgs (e.g. showContextMenu's x/y must be numbers) — a mapped
  // object type checks every property independently, unlike a single function
  // signature generic over the action name, which TS can't narrow inside its own
  // body. `arrowActionHandlers[action](...)` below is the one place that still
  // has to trust the runtime action string matches its args; everywhere else
  // (every real call site, via onAction's generic signature in CanvasArrow.tsx)
  // is fully type-checked.
  const arrowActionHandlers: { [K in CanvasArrowAction]: (arrow: Arrow, ...args: CanvasArrowActionArgs[K]) => void } = {
    hoverEnter: (arrow) => setHoveredArrow(arrow.id),
    hoverLeave: () => setHoveredArrow(null),
    delete: (arrow) => delArr(arrow.id),
    showContextMenu: (arrow, x, y) => setArrowContextMenu({ arrowId: arrow.id, x, y }),
  };
  arrowActionRef.current = (action, arrow, ...args) => {
    (arrowActionHandlers[action] as (arrow: Arrow, ...args: any[]) => void)(arrow, ...args);
  };
  const dispatchArrowAction = useCallback((action: CanvasArrowAction, arrow: Arrow, ...args: any[]) => {
    arrowActionRef.current(action, arrow, ...args);
  }, []);

  // Calculate best connection points between two boxes
  /**
   * Copies selected boxes and their connections to clipboard.
   * When cut is true, also removes the originals (and their arrows) from the canvas.
   */
  const copySelectedBoxes = React.useCallback((cut: boolean = false): void => {
    if (selectedBoxes.length === 0) return;
    if (cut && !canEditFlowchart) return;

    const boxesToCopy = boxes.filter(b => selectedBoxes.includes(b.id));
    const arrowsToCopy = arrows.filter(a =>
      selectedBoxes.includes(a.fromBox) && selectedBoxes.includes(a.toBox)
    );
    setClipboard({ boxes: boxesToCopy, arrows: arrowsToCopy });
    setLastPastedBoxes(null); // Reset multi-paste chain on new copy

    if (cut) {
      const newBoxes = boxes.filter(b => !selectedBoxes.includes(b.id));
      const newArrows = arrows.filter(a =>
        !selectedBoxes.includes(a.fromBox) && !selectedBoxes.includes(a.toBox)
      );
      pushHistoryEntry(newBoxes, newArrows);
      setBoxes(newBoxes);
      setArrows(newArrows);
      setSelectedBoxes([]);
    }
  }, [selectedBoxes, boxes, arrows, canEditFlowchart, pushHistoryEntry]);

  /**
   * Pastes boxes from clipboard with offset positioning
   */
  const pasteBoxes = React.useCallback((): void => {
    if (!clipboard || clipboard.boxes.length === 0) return;

    // Determine base boxes: use last pasted if available, otherwise use clipboard
    const baseBoxes = lastPastedBoxes || clipboard.boxes;
    const baseArrows = clipboard.arrows || [];
    
    // Create copies with new IDs and offset position
    const newBoxes: Box[] = [];
    const idMapping: Record<number, number> = {}; // Map old IDs to new IDs
    let newIdCounter = nextId;
    let newBoxCounter = nextBoxNum;
    let newCalloutCounter = nextCalloutNum;
    
    baseBoxes.forEach(box => {
      const newId = newIdCounter++;
      idMapping[box.id] = newId;
      
      const newBox = {
        ...box,
        id: newId,
        x: box.x + 50, // Offset by 50 pixels from last paste
        y: box.y + 50,
      };
      
      newBoxes.push(newBox);
      
      // Update counters for regular boxes and callouts
      if (box.type === 'regular') {
        newBoxCounter++;
      } else if (box.type === 'callout') {
        newCalloutCounter++;
      }
    });
    
    // Copy arrows between pasted boxes (use baseArrows from clipboard)
    const newArrows: Arrow[] = baseArrows.map(arrow => ({
      ...arrow,
      id: newIdCounter++,
      fromBox: idMapping[arrow.fromBox],
      toBox: idMapping[arrow.toBox]
    }));

    const finalBoxes = [...boxes, ...newBoxes];
    const finalArrows = [...arrows, ...newArrows];
    pushHistoryEntry(finalBoxes, finalArrows);
    setBoxes(finalBoxes);
    setArrows(finalArrows);
    setNextId(newIdCounter);
    setNextBoxNum(newBoxCounter);
    setNextCalloutNum(newCalloutCounter);

    // Select the newly pasted boxes
    setSelectedBoxes(newBoxes.map(b => b.id));

    // Store these as last pasted for next paste operation
    setLastPastedBoxes(newBoxes);
  }, [clipboard, lastPastedBoxes, boxes, arrows, nextId, nextBoxNum, nextCalloutNum, pushHistoryEntry]);


  const {
    ganttTasks, subitemGanttTasks, criticalPath, schedulingConflicts,
    calcEarliestStartOffset, endOffsetToDate,
    getStart, getEnd, getStartDate, getEndDate, ganttStartEndText,
    is24hRealStarts, renderedArrows, arrowBoxes, arrowBoxesById,
  } = useGanttSchedule({
    boxes: visibleBoxes, arrows: visibleArrows, durationUnit, fullDaySchedule, workHoursOnly, ganttWorkDays,
    ganttNonWorkingDays, ganttStartDate, workDayStartHour, workDayHoursPerDay,
    endTimeDisplay, showCriticalPathCanvas,
  });

  // Per-Area completion % (duration-weighted average of its member boxes', see
  // lib/gantt.ts's calcAreaCompletion) — drives the Area's canvas boundary turning
  // green at 100%, same completion source the Gantt's own Area-toplevel row uses
  // (GanttPanel.tsx/lib/svgExport.ts's makeAreaHeaderTask callbacks). Must sit
  // before the showGantt/showProjectGantt early returns below (Rules of Hooks) —
  // same constraint useGanttSchedule itself is already under.
  const areaCompletionById = useMemo(() => {
    const byBoxId = assignBoxesToAreas(visibleBoxes, areas);
    const membersByArea = new Map<number, typeof ganttTasks>();
    ganttTasks.forEach(t => {
      const areaId = byBoxId.get(t.id);
      if (areaId === undefined) return;
      if (!membersByArea.has(areaId)) membersByArea.set(areaId, []);
      membersByArea.get(areaId)!.push(t);
    });
    const result = new Map<number, number>();
    membersByArea.forEach((members, areaId) => result.set(areaId, calcAreaCompletion(members)));
    return result;
  }, [visibleBoxes, areas, ganttTasks]);

  const { exportAs } = useCanvasExport({
    boxes: topLevelBoxes, arrows: topLevelArrows, departments, areas, title, showLegend, showNotes,
    showStartDates, showFinishDates, showCriticalPathCanvas,
    showSubitemBadge, showUidBadge, subitemCountByBoxId, showAreas,
    criticalPath, schedulingConflicts, getStart, getEnd, getStartDate, getEndDate,
  });

  // MinimapOverlay is React.memo'd and only cares about position/size/type/color, but
  // `boxes` gets a new array reference on every edit (text, duration, assignee, status,
  // ...), which used to force it to re-scan every box's bounds and re-render up to
  // 100+ SVG rects on every one of those renders, not just when something visible on the
  // minimap actually changed. Same fingerprint-then-map pattern as useGanttSchedule's
  // arrowBoxes/arrowGeometryFingerprint.
  const minimapFingerprint = useMemo(
    () => topLevelBoxes.map(b => `${b.id}:${b.x}:${b.y}:${b.width}:${b.height}:${b.type}:${b.departmentColor}`).join('|'),
    [topLevelBoxes],
  );
  const minimapItems = useMemo(
    () => topLevelBoxes.map(b => ({ x: b.x, y: b.y, w: b.width, h: b.height, color: b.type === 'callout' ? '#eab308' : b.departmentColor, outlined: b.type === 'callout' })),
    [minimapFingerprint], // eslint-disable-line react-hooks/exhaustive-deps
  );

  useEffect(() => {
    if (linkPanel && linkInputRef.current) {
      const box = boxes.find(b => b.id === linkPanel.boxId);
      linkInputRef.current.value = box?.url || '';
      linkInputRef.current.focus();
      linkInputRef.current.select();
    }
  }, [linkPanel?.boxId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (uidPanel && uidInputRef.current) {
      const box = boxes.find(b => b.id === uidPanel.boxId);
      uidInputRef.current.value = box?.uid || '';
      uidInputRef.current.focus();
      uidInputRef.current.select();
    }
  }, [uidPanel?.boxId]); // eslint-disable-line react-hooks/exhaustive-deps

  useKeyboardShortcuts({
    isActiveRef, isEditingText, selectedBoxes, setSelectedBoxes, boxes, arrows, setBoxes, setArrows,
    copySelectedBoxes, pasteBoxes, undo, redo, pushHistoryEntry, saveRef,
    clickConnectMode, setClickConnectMode, setContextMenu, setLinkPanel,
  });

  // Reset editing state when clicking outside any input/textarea
  useEffect(() => {
    const handleDocMouseDown = (e: MouseEvent) => {
      // Skip synthetic mousedown events that the browser generates after a touch
      // (e.g. after a double-tap that set edit mode).
      if (isRecentTouchRef.current) return;
      const tag = (e.target as HTMLElement).tagName;
      if (tag !== 'INPUT' && tag !== 'TEXTAREA') {
        if (isEditingBoxTitle !== null) {
          setIsEditingBoxTitle(null);
          setIsEditingText(false);
        }
        // Unconditional (not just when isEditingBoxTitle is still set): a stray text
        // selection can outlive isEditingBoxTitle already having been reset by some
        // other path (e.g. a blur), and this click's whole point is to dismiss
        // exactly that leftover state — gating it on stale React state defeated it.
        window.getSelection()?.removeAllRanges();
      }
    };
    document.addEventListener('mousedown', handleDocMouseDown);
    return () => document.removeEventListener('mousedown', handleDocMouseDown);
  }, [isEditingBoxTitle]);

  // Save on browser close via a keepalive fetch (async doesn't work in beforeunload)
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      // Keepalive fetch: the browser still sends this request even after the tab is closed
      if (currentId && (boxes.length > 0 || arrows.length > 0)) {
        const token = sessionTokenRef.current;
        fetch(
          `${SUPABASE_URL}/rest/v1/flowcharts?id=eq.${currentId}`,
          {
            method: 'PATCH',
            keepalive: true,
            headers: {
              'Content-Type': 'application/json',
              'apikey': SUPABASE_ANON_KEY,
              'Authorization': `Bearer ${token ?? SUPABASE_ANON_KEY}`,
              'Prefer': 'return=minimal',
            },
            body: JSON.stringify({
              title,
              boxes,
              arrows,
              departments,
              duration_unit: durationUnit,
              updated_at: new Date().toISOString(),
              updated_by_email: user?.email ?? null,
            }),
          }
        ).catch(() => {});
      }

      // Warn when there's a new (unsaved) flowchart
      if ((boxes.length > 0 || arrows.length > 0) && !currentId) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }

    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [boxes, arrows, currentId, title, departments, durationUnit, user?.email]);

  // Session timeout: reload the flowchart if the tab was inactive for > 30 min
  useEffect(() => {
    const onVisible = async () => {
      if (document.visibilityState !== 'visible') return;
      const id = currentIdRef.current;
      if (!id || !lastLoadedAtRef.current) return;
      if (Date.now() - lastLoadedAtRef.current < 30 * 60 * 1000) return;
      // Flush any pending local edit BEFORE reloading. There's no flush-on-hide
      // listener here at all (unlike MindmapBuilder.tsx) — this file relies solely
      // on the debounced autosave effect (2-10s) to have already fired before the
      // tab was switched away. An edit made just before switching, within that
      // window, sits unsaved in memory for the whole time the tab is hidden; the
      // blind reload below used to discard it unconditionally the moment the tab
      // came back after 30+ minutes. saveNow() already merges against whatever
      // another user wrote in the meantime, so the reload right after it is safe
      // instead of a blind overwrite. Confirmed via a user report on the mindmap
      // equivalent of this exact listener (edit, switch tabs, return after a long
      // time, edit reverted) — same unconditional-reload shape, here too.
      await saveNow();
      if (currentIdRef.current !== id) return; // navigated away during the await
      const { data } = await supabase.from('flowcharts').select('*').eq('id', id).single();
      if (currentIdRef.current !== id) return;
      if (data && loadRef.current) loadRef.current(data);
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  // saveNow must be a real dependency (not a ref) — it closes over title/boxes/
  // arrows/etc directly (see useFlowchartSync's own saveNow), not via refs, so a
  // stale closure from an earlier render would flush stale content instead of
  // the user's actual latest edit. Re-subscribing the listener on every change is
  // cheap (just swaps which closure is attached) and correctness-critical here.
  }, [saveNow]);

  // Detect an autosave on mount — restore only via an explicit button, never automatically
  useEffect(() => {
    try {
      const data = lsGetJSON<any>(LS_KEYS.flowchartAutosave, null);
      if (!data) return;

      const ageHours = (Date.now() - data.timestamp) / (1000 * 60 * 60);
      if (ageHours > 24) {
        lsRemove(LS_KEYS.flowchartAutosave);
        return;
      }

      // Only show this if it was a new (unsaved) flowchart — ignore old autosaves without the marker
      if (data.newFlowchartOnly && (data.boxes?.length > 0 || data.arrows?.length > 0)) {
        setPendingAutosave(data);
      } else {
        lsRemove(LS_KEYS.flowchartAutosave);
      }
    } catch (error) {
      logger.error('Failed to detect autosave:', error);
      lsRemove(LS_KEYS.flowchartAutosave);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Moved above useBoxActions (which needs both as params) and above the
  // showProjectGantt/showGantt early returns below (Rules of Hooks) — both used to sit
  // much later, right next to the old boxActionHandlers object they fed into.
  const handleOpenStartConstraint = (boxId: number) => {
    const box = boxes.find(b => b.id === boxId);
    if (box?.earliestStart) { setStartConstraintModal({ boxId, type: 'earliest', value: box.earliestStart }); return; }
    if (box?.scheduledStart) { setStartConstraintModal({ boxId, type: 'fixed', value: box.scheduledStart }); return; }
    // No existing pin: default the calendar to the box's currently DISPLAYED
    // (computed, dependency-derived) start date instead of opening blank — picking
    // a nearby date then starts from where the box already is, not from "today".
    const d = getStartDate(boxId);
    const defaultDate = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    setStartConstraintModal({ boxId, type: 'fixed', value: defaultDate });
  };
  const handleContextMenuOpenAutoEnd = (boxId: number) => {
    const task = ganttTasks.find(t => t.id === boxId);
    let defaultVal = '';
    if (task) {
      const endDt = endOffsetToDate(task.start + task.duration, false);
      const pad = (n: number) => String(n).padStart(2, '0');
      defaultVal = `${endDt.getFullYear()}-${pad(endDt.getMonth() + 1)}-${pad(endDt.getDate())}T${pad(endDt.getHours())}:${pad(endDt.getMinutes())}`;
    }
    setAutoEndModal({ boxId, value: defaultVal });
  };

  const { dispatchBoxAction, updateResourceHours } = useBoxActions({
    canEditFlowchart, t,
    boxes, setBoxes, arrows, setArrows, nextId, setNextId,
    selectedBoxes, setSelectedBoxes, setHoveredBox, setDragging,
    setIsEditingBoxTitle, setIsEditingText, setContextMenu, setLinkPanel,
    editingDuration, setEditingDuration, workDayHoursPerDay,
    connecting, setConnecting, rightMouseDownPos,
    handleDown, handleOpenStartConstraint, handleContextMenuOpenAutoEnd, isPanningRef,
    handleOpenSubitems: (boxId: number) => setSubitemsPanelBoxId(boxId),
    pushHistoryEntry,
  });

  // Subitems panel (see Box.parentBoxId's own comment) — data + mutation handlers
  // for the popup opened via the box context menu's "Edit subitems" entry.
  const subitemsForPanel = useMemo(
    () => subitemsPanelBoxId === null ? [] : boxes.filter(b => b.parentBoxId === subitemsPanelBoxId),
    [boxes, subitemsPanelBoxId],
  );
  const subitemArrowsForPanel = useMemo(() => {
    const ids = new Set(subitemsForPanel.map(b => b.id));
    return arrows.filter(a => ids.has(a.fromBox) && ids.has(a.toBox));
  }, [arrows, subitemsForPanel]);

  // Keeps a parent box's own `duration` string in sync with its subitems, so it
  // reads correctly wherever `rolledUpDuration` isn't available (the box on the
  // main canvas, exports, ...) — rolledUpDuration itself (see lib/gantt.ts) is
  // still what actually drives the real outer schedule, computed fresh from
  // scratch in useGanttSchedule.ts; this is purely a display-string mirror of
  // that same computation, applied here so it's saved/persisted like any other
  // box field. Takes the boxes/arrows arrays as they'll be AFTER the caller's own
  // mutation, so every subitem handler below can compute this in the same
  // setBoxes pass instead of relying on stale-closure re-reads of React state.
  const syncParentDuration = (boxesAfter: Box[], arrowsAfter: Arrow[], parentId: number): Box[] => {
    const links = arrowsAfter.map(a => ({ id: a.id, fromBox: a.fromBox, toBox: a.toBox }));
    const rolled = calcSubitemDuration(parentId, boxesAfter, links, {
      durationUnit, fullDaySchedule, workHoursOnly, workDays: ganttWorkDays,
      nonWorkingDays: ganttNonWorkingDays, startDate: ganttStartDate,
      dayStartHour: workDayStartHour, hoursPerDay: workDayHoursPerDay,
    });
    if (rolled === null) return boxesAfter;
    const formatted = formatDuration(rolled, durationUnit);
    return boxesAfter.map(b => b.id === parentId ? { ...b, duration: formatted } : b);
  };

  const handleAddSubitem = () => {
    if (subitemsPanelBoxId === null) return;
    const id = nextId;
    // First subitem for this parent: its own duration field is about to become
    // disabled (see CanvasBox.tsx) — a one-off notice explains why, since a
    // disabled/grayed field alone doesn't say what changed at the moment it does.
    if (!boxes.some(b => b.parentBoxId === subitemsPanelBoxId)) {
      setDurationModeToast(t('durationNowFromSubitems'));
      setTimeout(() => setDurationModeToast(null), 4000);
    }
    const newBoxes = syncParentDuration([...boxes, {
      id, parentBoxId: subitemsPanelBoxId,
      title: '', text: '', duration: '1d', assignee: '', status: 'none', type: 'regular',
      x: 0, y: 0, width: 180, height: 110, departmentColor: '#dcfce7',
      completion: 0, importantFinish: false,
    }], arrows, subitemsPanelBoxId);
    pushHistoryEntry(newBoxes, arrows);
    setBoxes(newBoxes);
    setNextId(id + 1);
  };
  const handleUpdateSubitem = (id: number, patch: Partial<Box>) => {
    setBoxes(prev => {
      const next = prev.map(b => b.id === id ? { ...b, ...patch } : b);
      const parentId = prev.find(b => b.id === id)?.parentBoxId;
      return parentId !== undefined ? syncParentDuration(next, arrows, parentId) : next;
    });
  };
  // Not reusing useBoxActions' deleteBox here: it cascades to a deleted box's OWN
  // subitems (irrelevant for a subitem itself, which never has children) but
  // doesn't know about syncing the PARENT's duration — simpler to mirror its
  // (trivial, for a leaf) cascade locally than to chain a second setState pass
  // after it.
  const handleDeleteSubitem = (id: number) => {
    const parentId = boxes.find(b => b.id === id)?.parentBoxId;
    // Last subitem for this parent: its duration field is about to become
    // manually editable again — see the matching notice in handleAddSubitem above.
    if (parentId !== undefined && boxes.filter(b => b.parentBoxId === parentId).length === 1) {
      setDurationModeToast(t('durationNowManual'));
      setTimeout(() => setDurationModeToast(null), 4000);
    }
    const arrowsAfter = arrows.filter(a => a.fromBox !== id && a.toBox !== id);
    const boxesAfterDelete = boxes.filter(b => b.id !== id);
    const newBoxes = parentId !== undefined ? syncParentDuration(boxesAfterDelete, arrowsAfter, parentId) : boxesAfterDelete;
    pushHistoryEntry(newBoxes, arrowsAfter);
    setArrows(arrowsAfter);
    setBoxes(newBoxes);
  };
  const handleAddSubitemDependency = (fromBox: number, toBox: number) => {
    const id = nextId;
    const newArrow: Arrow = { id, fromBox, toBox, fromSide: 'right', toSide: 'left', dashed: false };
    const arrowsAfter = [...arrows, newArrow];
    const parentId = boxes.find(b => b.id === toBox)?.parentBoxId;
    const newBoxes = parentId !== undefined ? syncParentDuration(boxes, arrowsAfter, parentId) : boxes;
    pushHistoryEntry(newBoxes, arrowsAfter);
    setArrows(arrowsAfter);
    if (parentId !== undefined) setBoxes(newBoxes);
    setNextId(id + 1);
  };
  const handleRemoveSubitemDependency = (arrowId: number) => {
    const removedArrow = arrows.find(a => a.id === arrowId);
    const arrowsAfter = arrows.filter(a => a.id !== arrowId);
    const parentId = removedArrow ? boxes.find(b => b.id === removedArrow.toBox)?.parentBoxId : undefined;
    const newBoxes = parentId !== undefined ? syncParentDuration(boxes, arrowsAfter, parentId) : boxes;
    pushHistoryEntry(newBoxes, arrowsAfter);
    setArrows(arrowsAfter);
    if (parentId !== undefined) setBoxes(newBoxes);
  };

  // Gantt "Set timeline dates" saved presets (types/ganttUtils.ts's TimelinePreset)
  // — a shared list, not undo-tracked (matches resources/areas/departments' own
  // convention, not box/arrow edits). Defined here, before the showGantt early
  // return just below, so <GanttPanel>'s props can reference them.
  const handleAddTimelinePreset = (name: string, start: string, end: string) => {
    const preset: TimelinePreset = { id: `${Date.now()}-${Math.random().toString(36).slice(2)}`, name, start, end };
    const next = [...ganttTimelinePresets, preset];
    setGanttTimelinePresets(next);
    if (currentId) updateFlowchartMeta(currentId, { gantt_timeline_presets: next });
  };
  const handleUpdateTimelinePreset = (id: string, patch: Partial<Omit<TimelinePreset, 'id'>>) => {
    const next = ganttTimelinePresets.map(p => p.id === id ? { ...p, ...patch } : p);
    setGanttTimelinePresets(next);
    if (currentId) updateFlowchartMeta(currentId, { gantt_timeline_presets: next });
  };
  const handleDeleteTimelinePreset = (id: string) => {
    const next = ganttTimelinePresets.filter(p => p.id !== id);
    setGanttTimelinePresets(next);
    if (currentId) updateFlowchartMeta(currentId, { gantt_timeline_presets: next });
  };
  // "Filtered Export" saved row selections (types/ganttUtils.ts's ExportRowPreset) —
  // same shared-list convention as the timeline presets above.
  const handleAddExportRowPreset = (name: string, boxIds: number[], timelineRange?: { start: string; end: string } | null) => {
    const preset: ExportRowPreset = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2)}`, name, boxIds,
      ...(timelineRange ? { timelineStart: timelineRange.start, timelineEnd: timelineRange.end } : {}),
    };
    const next = [...ganttExportRowPresets, preset];
    setGanttExportRowPresets(next);
    if (currentId) updateFlowchartMeta(currentId, { gantt_export_row_presets: next });
  };
  const handleUpdateExportRowPreset = (id: string, patch: Partial<Omit<ExportRowPreset, 'id'>>) => {
    const next = ganttExportRowPresets.map(p => p.id === id ? { ...p, ...patch } : p);
    setGanttExportRowPresets(next);
    if (currentId) updateFlowchartMeta(currentId, { gantt_export_row_presets: next });
  };
  const handleDeleteExportRowPreset = (id: string) => {
    const next = ganttExportRowPresets.filter(p => p.id !== id);
    setGanttExportRowPresets(next);
    if (currentId) updateFlowchartMeta(currentId, { gantt_export_row_presets: next });
  };

  if (showProjectGantt && currentProjectId) {
    return (
      <ProjectGanttView
        projectId={currentProjectId}
        projectName={currentProjectName || 'Project'}
        user={user}
        onClose={() => setShowProjectGantt(false)}
      />
    );
  }

  if (showGantt) {
    return (
      <GanttPanel
        ganttTasks={ganttTasks}
        subitemGanttTasks={subitemGanttTasks}
        arrows={arrows}
        boxes={boxes}
        areas={areas}
        departments={departments}
        title={title}
        resources={resources}
        criticalPath={criticalPath}
        scheduleSettings={{
          durationUnit,
          ganttStartDate,
          workHoursOnly: ganttShowWorkDaysOnly,
          onWorkHoursOnlyChange: (v: boolean) => {
            setGanttShowWorkDaysOnly(v);
            if (currentId) {
              lsSet(lsHideNonWorkingDays(currentId), JSON.stringify(v));
              updateViewSetting(currentId, { gantt_hide_non_working_days: v });
            }
          },
          hideNonWorkingHours: ganttHideNonWorkingHours,
          onHideNonWorkingHoursChange: (v: boolean) => {
            setGanttHideNonWorkingHours(v);
            if (currentId) {
              lsSet(lsHideNonWorkingHours(currentId), JSON.stringify(v));
              updateViewSetting(currentId, { gantt_hide_non_working_hours: v });
            }
          },
          workDays: ganttWorkDays,
          fullDaySchedule,
          dayStartHour: workDayStartHour,
          hoursPerDay: workDayHoursPerDay,
          ganttNonWorkingDays,
        }}
        viewState={{
          onlyVisibleBars: ganttOnlyVisibleBars,
          onOnlyVisibleBarsChange: (v: boolean) => {
            setGanttOnlyVisibleBars(v);
            if (currentId) {
              lsSet(lsOnlyVisibleBars(currentId), JSON.stringify(v));
              updateViewSetting(currentId, { gantt_only_visible_bars: v });
            }
          },
          ganttOrder,
          onGanttOrderChange: setGanttOrder,
          initialLabelWidth: ganttLabelWidth,
          onLabelWidthChange: (width: number) => {
            setGanttLabelWidth(width);
            if (currentId) updateViewSetting(currentId, { gantt_label_width: width });
          },
          timelineView: ganttTimelineView,
          onTimelineViewChange: (v: { start: string; end: string } | null) => {
            setGanttTimelineView(v);
            if (currentId) updateViewSetting(currentId, { gantt_timeline_view: v });
          },
          timelinePresets: ganttTimelinePresets,
          onAddTimelinePreset: handleAddTimelinePreset,
          onUpdateTimelinePreset: handleUpdateTimelinePreset,
          onDeleteTimelinePreset: handleDeleteTimelinePreset,
          filteredExportEnabled: ganttFilteredExportEnabled,
          onFilteredExportEnabledChange: (v: boolean) => {
            setGanttFilteredExportEnabled(v);
            if (currentId) {
              lsSet(lsFilteredExportEnabled(currentId), JSON.stringify(v));
              updateViewSetting(currentId, { gantt_filtered_export_enabled: v });
            }
          },
          exportSelectedBoxIds: ganttExportSelectedBoxIds,
          onExportSelectedBoxIdsChange: (ids: number[]) => {
            setGanttExportSelectedBoxIds(ids);
            if (currentId) {
              lsSet(lsExportSelectedBoxIds(currentId), JSON.stringify(ids));
              updateViewSetting(currentId, { gantt_export_selected_box_ids: ids });
            }
          },
          exportRowPresets: ganttExportRowPresets,
          onAddExportRowPreset: handleAddExportRowPreset,
          onUpdateExportRowPreset: handleUpdateExportRowPreset,
          onDeleteExportRowPreset: handleDeleteExportRowPreset,
          initialVisibleLevels: ganttVisibleLevels,
          onVisibleLevelsChange: (levels: VisibleLevels) => {
            setGanttVisibleLevels(levels);
            if (currentId) updateViewSetting(currentId, { gantt_visible_levels: levels });
          },
          initialZoom: ganttZoom,
          onZoomChange: (zoom: number) => {
            setGanttZoom(zoom);
            if (currentId) updateViewSetting(currentId, { gantt_zoom: zoom });
          },
          resourceFilter: ganttResourceFilter,
          onResourceFilterChange: (filter: string[]) => {
            setGanttResourceFilter(filter);
            if (currentId) {
              lsSet(lsGanttResourceFilter(currentId), JSON.stringify(filter));
              updateViewSetting(currentId, { gantt_resource_filter: filter });
            }
          },
          categoryFilter: ganttCategoryFilter,
          onCategoryFilterChange: (filter: string[]) => {
            setGanttCategoryFilter(filter);
            if (currentId) {
              lsSet(lsGanttCategoryFilter(currentId), JSON.stringify(filter));
              updateViewSetting(currentId, { gantt_category_filter: filter });
            }
          },
          ganttDurationDisplay,
          onGanttDurationDisplayChange: (unit: string) => {
            const u = unit as DurationUnit;
            setGanttDurationDisplay(u);
            if (currentId) {
              lsSet(lsGanttDurationDisplay(currentId), JSON.stringify(u));
              updateViewSetting(currentId, { gantt_duration_display: u });
            }
          },
          showSubitemsInGantt,
          onShowSubitemsInGanttChange: (v: boolean) => {
            setShowSubitemsInGantt(v);
            if (currentId) {
              lsSet(lsShowSubitemsInGantt(currentId), JSON.stringify(v));
              updateViewSetting(currentId, { gantt_show_subitems: v });
            }
          },
          showAreaGroupsInGantt,
          onShowAreaGroupsInGanttChange: (v: boolean) => {
            setShowAreaGroupsInGantt(v);
            if (currentId) {
              lsSet(lsShowAreaGroupsInGantt(currentId), JSON.stringify(v));
              updateViewSetting(currentId, { gantt_show_areas: v });
            }
          },
          showRegularBoxesInGantt,
          onShowRegularBoxesInGanttChange: (v: boolean) => {
            setShowRegularBoxesInGantt(v);
            if (currentId) {
              lsSet(lsShowRegularBoxesInGantt(currentId), JSON.stringify(v));
              updateViewSetting(currentId, { gantt_show_regular_boxes: v });
            }
          },
          showStartEndColumnInGantt,
          onShowStartEndColumnInGanttChange: (v: boolean) => {
            setShowStartEndColumnInGantt(v);
            if (currentId) updateViewSetting(currentId, { gantt_show_start_end_column: v });
          },
        }}
        onClose={() => setShowGantt(false)}
        onShowInFlowplan={(boxId) => {
          setSelectedBoxes([boxId]);
          setPendingCenterBoxId(boxId);
          setShowGantt(false);
        }}
        calcEarliestStartOffset={calcEarliestStartOffset}
        endOffsetToDate={endOffsetToDate}
        is24hRealStarts={is24hRealStarts}
        startEndText={ganttStartEndText}
        onResourceHoursChange={updateResourceHours}
      />
    );
  }

  const canvasCriticalPathIds = criticalPath.boxIds;
  const canvasCriticalPathArrowIds = criticalPath.arrowIds;
  const canvasConflictBoxIds = schedulingConflicts.boxIds;
  const canvasConflictArrowIds = schedulingConflicts.arrowIds;

  // ── CanvasModals pre-wrapped callbacks ──────────────────────────────────────

  const handleSaveLink = (boxId: number, url: string) => {
    const newBoxes = boxes.map(b => b.id === boxId ? { ...b, url: url || undefined } : b);
    pushHistoryEntry(newBoxes, arrows);
    setBoxes(newBoxes);
  };
  const handleDeleteLink = (boxId: number) => {
    const newBoxes = boxes.map(b => b.id === boxId ? { ...b, url: undefined } : b);
    pushHistoryEntry(newBoxes, arrows);
    setBoxes(newBoxes);
  };

  // Works for both a top-level box and a subitem (same flat `boxes` array,
  // see Box.parentBoxId's own comment) — no special-casing needed either way.
  const handleSaveUid = (boxId: number, uid: string) => {
    const newBoxes = boxes.map(b => b.id === boxId ? { ...b, uid: uid.trim() || undefined } : b);
    pushHistoryEntry(newBoxes, arrows);
    setBoxes(newBoxes);
  };
  const handleDeleteUid = (boxId: number) => {
    const newBoxes = boxes.map(b => b.id === boxId ? { ...b, uid: undefined } : b);
    pushHistoryEntry(newBoxes, arrows);
    setBoxes(newBoxes);
  };

  const handleContextMenuToggle24_7 = (boxIds: number[]) => {
    if (!canEditFlowchart) return;
    const allOn = boxIds.every(id => boxes.find(b => b.id === id)?.alwaysOn);
    const newBoxes = boxes.map(b => {
      if (!boxIds.includes(b.id)) return b;
      const next = !allOn;
      return { ...b, alwaysOn: next, is24_5: next ? false : b.is24_5 };
    });
    pushHistoryEntry(newBoxes, arrows);
    setBoxes(newBoxes);
  };
  const handleContextMenuToggle24_5 = (boxIds: number[]) => {
    if (!canEditFlowchart) return;
    const allOn = boxIds.every(id => boxes.find(b => b.id === id)?.is24_5);
    const newBoxes = boxes.map(b => {
      if (!boxIds.includes(b.id)) return b;
      const next = !allOn;
      return { ...b, is24_5: next, alwaysOn: next ? false : b.alwaysOn };
    });
    pushHistoryEntry(newBoxes, arrows);
    setBoxes(newBoxes);
  };
  const handleContextMenuToggleMilestone = (boxIds: number[]) => {
    if (!canEditFlowchart) return;
    const allOn = boxIds.every(id => boxes.find(b => b.id === id)?.importantFinish);
    const newBoxes = boxes.map(b => boxIds.includes(b.id) ? { ...b, importantFinish: !allOn } : b);
    pushHistoryEntry(newBoxes, arrows);
    setBoxes(newBoxes);
  };
  const handleContextMenuCopy = (boxIds: number[]) => {
    const boxesToCopy = boxes.filter(b => boxIds.includes(b.id));
    const arrowsToCopy = arrows.filter(a => boxIds.includes(a.fromBox) && boxIds.includes(a.toBox));
    setClipboard({ boxes: boxesToCopy, arrows: arrowsToCopy });
    setLastPastedBoxes(null);
  };
  const handleContextMenuDuplicate = (boxIds: number[]) => {
    const toDup = boxes.filter(b => boxIds.includes(b.id));
    const idMap: Record<number, number> = {};
    let id = nextId;
    const dupedBoxes = toDup.map(b => { idMap[b.id] = id; return { ...b, id: id++, x: b.x + 20, y: b.y + 20 }; });
    const dupedArrows = arrows.filter(a => boxIds.includes(a.fromBox) && boxIds.includes(a.toBox))
      .map(a => ({ ...a, id: id++, fromBox: idMap[a.fromBox], toBox: idMap[a.toBox] }));
    const finalBoxes = [...boxes, ...dupedBoxes];
    const finalArrows = [...arrows, ...dupedArrows];
    pushHistoryEntry(finalBoxes, finalArrows);
    setBoxes(finalBoxes);
    setArrows(finalArrows);
    setNextId(id);
    setSelectedBoxes(dupedBoxes.map(b => b.id));
  };
  const handleContextMenuOpenResizeNote = (boxId: number) => {
    const box = boxes.find(b => b.id === boxId);
    if (box) setResizeNoteModal({ boxId, width: box.width, height: box.height });
  };
  // "Make private comment" — see Box.privateTo's own comment. Only ever set/cleared
  // against the CURRENT user's own id, and only for callout-type boxes (enforced
  // here, not just by the menu item only appearing for callouts, since this is the
  // one place that actually writes the field).
  const handleContextMenuTogglePrivate = (boxId: number) => {
    if (!user?.id) return;
    const newBoxes = boxes.map(b => {
      if (b.id !== boxId || b.type !== 'callout') return b;
      return b.privateTo === user.id ? { ...b, privateTo: undefined } : { ...b, privateTo: user.id };
    });
    pushHistoryEntry(newBoxes, arrows);
    setBoxes(newBoxes);
  };
  // Inlined rather than looping useBoxActions' deleteBox (which now pushes its own
  // history entry per call, see its own comment) — a multi-select delete must land
  // as ONE undo step, not one per box. Mirrors deleteBox's own subitem-cascade and
  // in-progress-connection-clear side effects, just batched over every id at once.
  const handleContextMenuDelete = (boxIds: number[]) => {
    if (connecting && boxIds.includes(connecting.boxId)) setConnecting(null);
    const removedIds = new Set([
      ...boxIds,
      ...boxes.filter(b => b.parentBoxId !== undefined && boxIds.includes(b.parentBoxId)).map(b => b.id),
    ]);
    const newBoxes = boxes.filter(b => !removedIds.has(b.id));
    const newArrows = arrows.filter(a => !removedIds.has(a.fromBox) && !removedIds.has(a.toBox));
    pushHistoryEntry(newBoxes, newArrows);
    setBoxes(newBoxes);
    setArrows(newArrows);
    setSelectedBoxes([]);
  };

  const handleSaveStartConstraint = (boxId: number, type: 'fixed' | 'earliest', value: string) => {
    if (type === 'fixed' && value) {
      const d = new Date(value.split('T')[0]);
      if (!isNaN(d.getTime()) && d < ganttStartDate) {
        setGanttStartDate(d);
        if (currentId) {
          const ds = value.split('T')[0];
          lsSet(lsGanttStartDate(currentId), ds);
          updateFlowchartMeta(currentId, { gantt_start_date: ds });
        }
      }
    }
    const newBoxes = boxes.map(b => {
      if (b.id !== boxId) return b;
      return type === 'fixed'
        ? { ...b, scheduledStart: value || undefined, earliestStart: undefined }
        : { ...b, earliestStart: value || undefined, scheduledStart: undefined };
    });
    pushHistoryEntry(newBoxes, arrows);
    setBoxes(newBoxes);
    setStartConstraintModal(null);
  };

  const handleSaveAutoEnd = (boxId: number, endValue: string) => {
    if (!endValue) { setAutoEndModal(null); return; }
    const box = boxes.find(b => b.id === boxId);
    const task = ganttTasks.find(t => t.id === boxId);
    if (!box || !task) { setAutoEndModal(null); return; }

    // Determine actual start datetime
    let startDt: Date;
    if (box.scheduledStart) {
      const [datePart, timePart] = box.scheduledStart.split('T');
      const [sy, sm, sd] = datePart.split('-').map(Number);
      startDt = new Date(sy, sm - 1, sd, fullDaySchedule ? 0 : workDayStartHour, 0, 0);
      if (timePart) { const [h, m] = timePart.split(':').map(Number); startDt.setHours(h || 0, m || 0, 0, 0); }
    } else {
      startDt = endOffsetToDate(task.start, true);
    }

    // Parse user-selected end datetime
    const [datePart, timePart] = endValue.split('T');
    const [ey, em, ed] = datePart.split('-').map(Number);
    const endDt = new Date(ey, em - 1, ed, fullDaySchedule ? 24 : workDayStartHour + workDayHoursPerDay, 0, 0);
    if (timePart) { const [h, m] = timePart.split(':').map(Number); endDt.setHours(h || 0, m || 0, 0, 0); }

    const dayStartH = fullDaySchedule ? 0 : workDayStartHour;
    const hpd = fullDaySchedule ? 24 : workDayHoursPerDay;
    const hours = workHoursBetween(
      startDt, endDt,
      box.alwaysOn ?? false,
      box.is24_5 ?? false,
      fullDaySchedule,
      ganttNonWorkingDays,
      ganttWorkDays,
      dayStartH,
      hpd,
    );

    // Pick whichever unit keeps the number small (>24h -> days, >7d -> weeks), rather
    // than always following the flowchart's global durationUnit setting — box.duration
    // is self-describing (its own suffix), so this doesn't need to match that setting.
    // parseDuration uses the configured workday length as base for 'd'/'w' suffixes,
    // so dividing by that (or 5x that, for a week) here produces the correct gantt
    // columns regardless of which unit ends up chosen.
    //
    // Round to the app's minimum granularity (half an hour) FIRST, in hour-space, and
    // derive days/weeks from that rounded value — rounding days/weeks independently to
    // a fixed number of decimals (the old approach) can land the reconstructed hours
    // (parseDuration multiplies back by hoursPerDay) on an arbitrary number of minutes
    // instead of a clean half hour, e.g. a 3-day task showing up as "4.7d" that decodes
    // to 37.6h — 6 minutes off a half-hour boundary.
    const roundedHours = Math.max(0.5, Math.round(hours * 2) / 2);
    let durationStr: string;
    const forceHours = box.alwaysOn || box.is24_5;
    if (!forceHours && roundedHours > 24 * 7) {
      durationStr = `${roundedHours / (workDayHoursPerDay * 5)}w`;
    } else if (!forceHours && roundedHours > 24) {
      durationStr = `${roundedHours / workDayHoursPerDay}d`;
    } else {
      durationStr = `${roundedHours}h`;
    }
    const newBoxes = boxes.map(b => b.id === boxId ? { ...b, duration: durationStr } : b);
    pushHistoryEntry(newBoxes, arrows);
    setBoxes(newBoxes);
    setAutoEndModal(null);
  };

  const handleApplyResizeNote = (boxId: number, width: number, height: number) => {
    const newBoxes = boxes.map(b => b.id === boxId ? { ...b, width: Math.max(120, Math.min(800, width)), height: Math.max(80, Math.min(600, height)) } : b);
    pushHistoryEntry(newBoxes, arrows);
    setBoxes(newBoxes);
    setResizeNoteModal(null);
  };

  const handleDismissAutosave = () => {
    lsRemove(LS_KEYS.flowchartAutosave);
    setPendingAutosave(null);
  };

  const handleCopyToFlowchart = async (targetId: string): Promise<{ success: boolean; error?: string }> => {
    if (!copyToFlowchartModal) return { success: false };
    const boxesToCopy = boxes.filter(b => copyToFlowchartModal.boxIds.includes(b.id));
    const arrowsToCopy = arrows.filter(a =>
      copyToFlowchartModal.boxIds.includes(a.fromBox ?? a.from ?? -1) &&
      copyToFlowchartModal.boxIds.includes(a.toBox ?? a.to ?? -1)
    );

    const { data: target, error: fetchErr } = await supabase
      .from('flowcharts').select('boxes, arrows, data').eq('id', targetId).single();
    if (fetchErr || !target) return { success: false, error: 'Could not load target flowchart.' };

    const tBoxes: any[] = Array.isArray(target.boxes) ? target.boxes : [];
    const tArrows: any[] = Array.isArray(target.arrows) ? target.arrows : [];
    const maxId = Math.max(
      ...(tBoxes.length ? tBoxes.map((b: any) => b.id) : [0]),
      ...(tArrows.length ? tArrows.map((a: any) => a.id) : [0]),
      target.data?.nextId ?? 0,
    );
    let id = maxId + 1;

    const idMap: Record<number, number> = {};
    const newBoxes = boxesToCopy.map(b => { idMap[b.id] = id; return { ...b, id: id++ }; });
    const newArrows = arrowsToCopy.map(a => ({
      ...a, id: id++,
      fromBox: idMap[a.fromBox ?? a.from ?? 0],
      toBox: idMap[a.toBox ?? a.to ?? 0],
      from: undefined, to: undefined,
    }));

    const { error: saveErr } = await supabase.from('flowcharts').update({
      boxes: [...tBoxes, ...newBoxes],
      arrows: [...tArrows, ...newArrows],
      data: { ...(target.data || {}), nextId: id },
    }).eq('id', targetId);

    if (saveErr) return { success: false, error: 'Failed to save. Check your access rights.' };
    return { success: true };
  };

  return (
    <div className="w-full bg-blue-50 flex flex-col" style={{ height: '100dvh' }}>
      {syncFailed && (
        <div style={{ position: 'fixed', top: 8, left: '50%', transform: 'translateX(-50%)', zIndex: 9999, background: '#dc2626', color: 'white', fontSize: 13, fontWeight: 600, padding: '6px 16px', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.25)', display: 'flex', alignItems: 'center', gap: 8 }}>
          <span>Changes not saved yet — retrying…</span>
        </div>
      )}
      <CanvasTopbar
        title={title}
        onTitleChange={setTitle}
        onNew={newFlow}
        onOpen={() => onOpenProjectList({
          onLoadFlowchart: async (f) => { const { data } = await supabase.from('flowcharts').select('*').eq('id', f.id).single(); if (data) await load(data); setCurrentId(f.id); setTitle(f.title); },
          onDeleteFlowchart: del,
          onRestoreVersion: handleRestoreVersion,
          onSetProject: (pid, name) => { setCurrentProjectId(pid); setCurrentProjectName(name ?? null); },
          currentFlowchartId: currentId ?? undefined,
        })}
        onSave={save}
        isSaving={isSaving}
        onShowHistory={() => { setShowHistory(true); loadChangeLog(); }}
        onShowShare={() => setShowShareDialog(true)}
        currentId={currentId}
        userRole={userRole}
showHelpPanel={showHelpPanel}
        onToggleHelpPanel={() => setShowHelpPanel(p => !p)}
        user={user}
        onSignOut={onSignOut}
        onlineUsers={onlineUsers}
        durationUnit={durationUnit}
        onDurationUnitChange={(u) => {
          // Deliberately does NOT touch endTimeDisplay anymore — it's a personal
          // setting now, durationUnit is shared; see onEndTimeDisplayChange's own
          // comment for the full reasoning. Date mode works under any durationUnit
          // (getStart/getEnd in useGanttSchedule.ts already derive real clock dates
          // for days/weeks too), so no cross-setting restriction is needed here.
          setDurationUnit(u as DurationUnit);
        }}
        endTimeDisplay={endTimeDisplay}
        onEndTimeDisplayChange={(v) => {
          // Deliberately does NOT force durationUnit anymore. Before this setting
          // became personal, picking a date mode forcing the (then also shared)
          // durationUnit to 'hours' was safe — now it would let one user's personal
          // display choice silently rewrite the shared scheduling granularity for
          // every collaborator. See onDurationUnitChange's comment for the other
          // half of this decoupling. CanvasTopbar passes the full next EndTimeDisplay
          // object here on every change (mode switch or individual checkbox toggle).
          setEndTimeDisplay(v);
          if (currentId) updateViewSetting(currentId, { end_time_display: v });
        }}
        showNotes={showNotes}
        onToggleNotes={() => {
          const v = !showNotes;
          setShowNotes(v);
          if (currentId) {
            lsSet(lsCanvasShowComments(currentId), JSON.stringify(v));
            updateViewSetting(currentId, { canvas_show_comments: v });
          }
        }}
        showSubitemBadge={showSubitemBadge}
        onToggleSubitemBadge={() => {
          const v = !showSubitemBadge;
          setShowSubitemBadge(v);
          if (currentId) {
            lsSet(lsCanvasShowSubitemBadges(currentId), JSON.stringify(v));
            updateViewSetting(currentId, { canvas_show_subitem_badges: v });
          }
        }}
        showUidBadge={showUidBadge}
        onToggleUidBadge={() => {
          const v = !showUidBadge;
          setShowUidBadge(v);
          if (currentId) {
            lsSet(lsCanvasShowUidBadges(currentId), JSON.stringify(v));
            updateViewSetting(currentId, { canvas_show_uid_badges: v });
          }
        }}
        showAreas={showAreas}
        onToggleAreas={() => {
          const v = !showAreas;
          setShowAreas(v);
          if (currentId) {
            lsSet(lsCanvasShowAreas(currentId), JSON.stringify(v));
            updateViewSetting(currentId, { canvas_show_areas: v });
          }
        }}
        viewAllFields={viewAllFields}
        onToggleViewAllFields={() => {
          const v = !viewAllFields;
          setViewAllFields(v);
          if (currentId) updateViewSetting(currentId, { canvas_view_all_fields: v });
        }}
        connectActive={!!clickConnectMode}
        onToggleConnect={() => setClickConnectMode(clickConnectMode ? null : {})}
        onShowPlanSettings={() => setShowPlanSettings(true)}
        onShowGantt={() => setShowGantt(true)}
        currentProjectId={currentProjectId}
        currentProjectName={currentProjectName}
        onShowProjectGantt={() => setShowProjectGantt(true)}
        zoom={zoom}
        onZoomIn={() => applyZoom(prev => Math.min(prev + 0.1, 2))}
        onZoomOut={() => applyZoom(prev => Math.max(prev - 0.1, 0.2))}
      />
      {/* Main area: sidebar + canvas */}
      <div className="flex flex-1 overflow-hidden">

        <CanvasSidebar
          onAddBox={addBox}
          onAddNote={addCallout}
          onAddStart={addStartDay}
          showLegend={showLegend}
          onToggleLegend={() => setShowLegend(!showLegend)}
          showResources={showResources}
          onToggleResources={() => setShowResources(!showResources)}
          isDrawingAreaMode={isDrawingAreaMode}
          onToggleAreaMode={() => { setIsDrawingAreaMode(v => !v); setDrawingArea(null); }}
          onUndo={undo}
          onRedo={redo}
          showCriticalPath={showCriticalPathCanvas}
          onToggleCriticalPath={() => setShowCriticalPathCanvas(v => !v)}
          canExport={boxes.length > 0}
          onExportClick={(e) => { setExportMenuY(e.currentTarget.getBoundingClientRect().top); setShowExportMenu(v => !v); }}
          currentId={currentId}
          defaultFlowchartId={defaultFlowchartId}
          onToggleHome={handleToggleHome}
          onSwitchToMindmap={onSwitchToMindmap}
          version={APP_VERSION}
        />

      <div
        className="flex-1 relative overflow-hidden"
        style={{ touchAction: 'none' }}
        ref={canvasRef}
        onTouchStart={(e) => {
          // Close context menu and deselect boxes via React's synthetic handler
          // Guards prevent unnecessary re-renders during pan/zoom.
          if (contextMenu !== null) setContextMenu(null);
          if (arrowContextMenu !== null) setArrowContextMenu(null);
          const isBox = !!(e.target as Element).closest('[data-box-el]');
          const isArrow = !!(e.target as Element).closest('[data-arrow-el]');
          if (!isBox && !isArrow) {
            // Touch on empty canvas — close edit mode, connecting mode and clear selection
            if (isEditingBoxTitle !== null) { setIsEditingBoxTitle(null); setIsEditingText(false); }
            if (connecting !== null) setConnecting(null);
            if (selectedBoxes.length > 0) setSelectedBoxes([]);
            // Clear hover state so connector points disappear after touch
            if (hoveredBox !== null) setHoveredBox(null);
          }
        }}
        onMouseMove={(e) => {
          // Skipped while panning: getBoundingClientRect() below forces a
          // synchronous layout flush on every single mousemove, which is real cost
          // on a large flowchart (hundreds of boxes + a big arrow SVG) — and
          // panOffset/zoom (React state) don't update live during a pan anyway (see
          // useCanvasPointerEvents.ts's own comment), so the broadcast position
          // computed here would be stale/wrong regardless. See useBoxActions.ts's
          // mouseEnter/mouseLeave guard for the other half of this same fix.
          if (!canvasRef.current || !currentId || isPanningRef.current) return;
          const rect = canvasRef.current.getBoundingClientRect();
          const canvasX = (e.clientX - rect.left - panOffset.x) / zoom;
          const canvasY = (e.clientY - rect.top - panOffset.y) / zoom;
          broadcastCursor(canvasX, canvasY);
        }}
        onMouseDown={(e) => {
          // Blur any focused input/textarea so cursor stops blinking when clicking canvas —
          // but only when the click itself landed OUTSIDE that field. This handler sees
          // every mousedown on the canvas, including ones on an already-focused field that
          // lives inside it (e.g. an Area title's <input>, rendered via foreignObject) —
          // without this guard, clicking a SECOND time inside that same input (to move the
          // caret somewhere in the middle of the text) blurred it before the browser could
          // place the caret, making it impossible to click anywhere but the end of the text.
          if (document.activeElement instanceof HTMLElement &&
              (document.activeElement.tagName === 'INPUT' || document.activeElement.tagName === 'TEXTAREA') &&
              !document.activeElement.contains(e.target as Node)) {
            document.activeElement.blur();
          }
          // Close the context menu on any click on the canvas (or a box). Guarded
          // (not called unconditionally) because calling a state setter with the
          // same value it already holds still re-renders THIS component once (React
          // only bails out of going deeper into children, not of re-running this
          // function's own body) — on this file's size that "wasted" render alone
          // was a confirmed, profiled long task (useGanttSchedule's
          // getAlwaysOnClockStart doing recursive O(n) lookups over every task) that
          // fired on literally every mousedown, including every single pan-start,
          // since contextMenu is normally already null at that point. The sibling
          // touch handler already had this exact guard; this mouse one didn't.
          if (contextMenu !== null) setContextMenu(null);
          // If draft box exists, place it on click
          if (draftBox) {
            e.preventDefault();
            // A callout placed while "Comments" is off in View settings is immediately
            // filtered out of the render (see the topLevelBoxes.filter(... showNotes ...)
            // below) — not lost, just invisible, but that reads exactly like the comment
            // silently vanished. Warn instead of placing it with no explanation.
            if (draftBox.type === 'callout' && !showNotes) alert(t('commentHiddenInViewWarning'));
            const newBoxes = [...boxes, draftBox as unknown as Box];
            // Save the new state directly to history (avoids a race condition with the 800ms debounce)
            pushHistoryEntry(newBoxes, arrows);
            setBoxes(newBoxes);
            setNextId(nextId + 1);
            if (draftBox.type === 'regular') setNextBoxNum(nextBoxNum + 1);
            if (draftBox.type === 'callout') setNextCalloutNum(nextCalloutNum + 1);
            setDraftBox(null);
            return;
          }
          
          // Previously also matched any (e.target as Element).tagName === 'svg', meant
          // to catch a click landing on the big arrows-drawing SVG's own body — but
          // that SVG (and the Areas one) are both pointer-events:none on themselves
          // (only individual paths/handles opt back in), so a mousedown's e.target can
          // never actually BE one of those two SVGs anymore; the check was dead for its
          // original purpose. What it was NOT dead for: every lucide-react icon renders
          // a bare <svg> too, and clicking anywhere in a small icon's mostly-empty
          // viewBox (e.g. the resource-assignment dropdown's chevron) commonly lands
          // directly on that <svg> rather than on a <path> — tagName check couldn't
          // tell the two apart, so it misfired as "clicked empty canvas", deselecting
          // the box whose chevron the user just clicked.
          const isCanvasClick = e.target === e.currentTarget ||
                                (e.target as Element).id === 'flowchart-canvas';
          
          // Reset editing state on mousedown — but NOT if this is a synthetic mouse event after a touch
          // (isRecentTouchRef prevents the edit mode set by a double-tap from being immediately canceled)
          if (!isRecentTouchRef.current) {
            if (isEditingBoxTitle !== null) {
              setIsEditingText(false);
              setIsEditingBoxTitle(null);
            }
            // Unconditional on isEditingBoxTitle — see the document-mousedown handler's
            // matching comment above for why a stale selection can outlive that state.
            window.getSelection()?.removeAllRanges();
          }
          
          // Start panning with right mouse button or middle mouse button
          if (isCanvasClick && (e.button === 2 || e.button === 1)) {
            e.preventDefault();
            rightMouseDownPos.current = { x: e.clientX, y: e.clientY };
            startPanning(e.clientX, e.clientY);
            return;
          }
          
          // Area drawing mode: start a drag to draw an area
          if (isDrawingAreaMode && e.button === 0) {
            e.preventDefault();
            setSelectedAreaId(null);
            setEditingAreaId(null);
            const rect = canvasRef.current?.getBoundingClientRect();
            if (!rect) return;
            const startX = (e.clientX - rect.left - panOffset.x) / zoom;
            const startY = (e.clientY - rect.top - panOffset.y) / zoom;
            setDrawingArea({ startX, startY, currentX: startX, currentY: startY });
            return;
          }

          // Start drag-to-select when clicking on empty canvas with left button
          if (isCanvasClick && e.button === 0) {
            e.preventDefault();
            // Cancel connecting mode on click on empty canvas
            if (connecting !== null) setConnecting(null);
            const rect = canvasRef.current?.getBoundingClientRect();
            if (!rect) return;
            const startX = (e.clientX - rect.left - panOffset.x) / zoom;
            const startY = (e.clientY - rect.top - panOffset.y) / zoom;

            setSelectionRect({
              startX,
              startY,
              currentX: startX,
              currentY: startY
            });
            setSelectedBoxes([]);
            setSelectedAreaId(null);
            setEditingAreaId(null);
            setContextMenu(null);
            setLinkPanel(null);
            setShowHistory(false);
          }
        }}
        onContextMenu={(e) => {
          e.preventDefault();
          const down = rightMouseDownPos.current;
          if (down && (Math.abs(e.clientX - down.x) > 5 || Math.abs(e.clientY - down.y) > 5)) {
            rightMouseDownPos.current = null;
            return; // was a pan gesture, not a click
          }
          rightMouseDownPos.current = null;
          setContextMenu({ x: e.clientX, y: e.clientY, type: 'canvas' });
        }}
      >
        <div id="flowchart-canvas" ref={canvasContentRef} style={{
          transform: `translate(${panOffset.x}px, ${panOffset.y}px) scale(${zoom})`,
          transformOrigin: 'top left',
          width: `${100 / zoom}%`,
          height: `${100 / zoom}%`,
          cursor: isDrawingAreaMode ? 'crosshair' : 'default',
          // This element (and its 20000×20000px absolutely-positioned SVG child,
          // see below) is exactly the kind of element that needs its own persistent
          // GPU compositor layer: it's transformed on every pan/zoom frame via
          // direct DOM writes (see useCanvasPointerEvents.ts) specifically to avoid
          // React re-renders. Without willChange, a browser can DEMOTE an idle
          // layer back to normal painting after it hasn't been transformed for a
          // while — then re-promoting it (rasterizing this whole large virtual
          // canvas again) at the start of the next pan is real, visible work on a
          // large flowchart, matching the reported "smooth once dragging, but the
          // first second of every new pan is slow" pattern. willChange keeps the
          // layer alive so that promotion cost is paid once, not on every gesture.
          willChange: 'transform',
        }}>
          <PresenceLayer subscribeCursors={subscribeCursors} />
          <svg 
            className="absolute pointer-events-none" 
            style={{ 
              zIndex: 1, 
              left: '-5000px',
              top: '-5000px',
              width: '20000px',
              height: '20000px'
            }}
            viewBox="-5000 -5000 20000 20000"
          >
            {/* Area drawing preview — rendered here (not in the always-on-top Areas SVG
                below) so it stays visible while actively drawing a new area even when
                "Show Areas" is toggled off. */}
            {drawingArea && (() => {
              const x = Math.min(drawingArea.startX, drawingArea.currentX);
              const y = Math.min(drawingArea.startY, drawingArea.currentY);
              const w = Math.abs(drawingArea.currentX - drawingArea.startX);
              const h = Math.abs(drawingArea.currentY - drawingArea.startY);
              return <rect x={x} y={y} width={w} height={h} fill="rgba(99,102,241,0.05)" stroke="#6366f1" strokeWidth={2} strokeDasharray="10,6" style={{ pointerEvents: 'none' }} />;
            })()}

            {connecting && (() => {
              const connectingBox = boxes.find(b => b.id === connecting.boxId);
              if (!connectingBox) return null; // box was deleted while connecting was active
              const isCallout = connectingBox.type === 'callout';
              const startPoint = getConnPt(connectingBox, connecting.side);
              
              let pathD;
              if (isCallout) {
                pathD = `M ${startPoint.x} ${startPoint.y} L ${mousePos.x} ${mousePos.y}`;
              } else {
                // If hovering over a box, use its actual connection points
                // Otherwise, guess based on mouse position
                const fromSide = connecting.side;
                let likelyToSide;
                let targetPoint = mousePos;
                
                if (hoveredBox) {
                  // User is hovering over a box - find its closest connection point to MOUSE
                  const targetBox = boxes.find(b => b.id === hoveredBox);
                  if (targetBox) {
                    // Calculate all connection points of the hovered box
                    const connPoints = {
                      left: getConnPt(targetBox, 'left'),
                      right: getConnPt(targetBox, 'right'),
                      top: getConnPt(targetBox, 'top'),
                      bottom: getConnPt(targetBox, 'bottom')
                    };
                    
                    // Find closest connection point to MOUSE position (where user will click)
                    let closestSide: keyof typeof connPoints = 'left';
                    let minDist = Infinity;

                    (Object.entries(connPoints) as [keyof typeof connPoints, Point][]).forEach(([side, point]) => {
                      const dist = Math.sqrt(
                        Math.pow(point.x - mousePos.x, 2) +
                        Math.pow(point.y - mousePos.y, 2)
                      );
                      if (dist < minDist) {
                        minDist = dist;
                        closestSide = side;
                      }
                    });
                    
                    likelyToSide = closestSide;
                    targetPoint = connPoints[closestSide];
                  } else {
                    // Fallback if box not found
                    const dx = mousePos.x - startPoint.x;
                    const dy = mousePos.y - startPoint.y;
                    likelyToSide = Math.abs(dx) > Math.abs(dy) 
                      ? (dx > 0 ? 'left' : 'right')
                      : (dy > 0 ? 'top' : 'bottom');
                  }
                } else {
                  // Not hovering over a box - guess based on direction
                  const dx = mousePos.x - startPoint.x;
                  const dy = mousePos.y - startPoint.y;
                  likelyToSide = Math.abs(dx) > Math.abs(dy) 
                    ? (dx > 0 ? 'left' : 'right')
                    : (dy > 0 ? 'top' : 'bottom');
                  targetPoint = mousePos;
                }
                
                // Draw path using standard arrow logic
                if (fromSide === 'left' || fromSide === 'right') {
                  if (likelyToSide === 'left' || likelyToSide === 'right') {
                    const midX = (startPoint.x + targetPoint.x) / 2;
                    pathD = `M ${startPoint.x} ${startPoint.y} L ${midX} ${startPoint.y} L ${midX} ${targetPoint.y} L ${targetPoint.x} ${targetPoint.y}`;
                  } else {
                    pathD = `M ${startPoint.x} ${startPoint.y} L ${targetPoint.x} ${startPoint.y} L ${targetPoint.x} ${targetPoint.y}`;
                  }
                } else {
                  if (likelyToSide === 'top' || likelyToSide === 'bottom') {
                    const midY = (startPoint.y + targetPoint.y) / 2;
                    pathD = `M ${startPoint.x} ${startPoint.y} L ${startPoint.x} ${midY} L ${targetPoint.x} ${midY} L ${targetPoint.x} ${targetPoint.y}`;
                  } else {
                    pathD = `M ${startPoint.x} ${startPoint.y} L ${startPoint.x} ${targetPoint.y} L ${targetPoint.x} ${targetPoint.y}`;
                  }
                }
              }
              
              return (
                <path
                  d={pathD}
                  stroke={isCallout ? "#eab308" : "#3b82f6"}
                  strokeWidth="2"
                  fill="none"
                  strokeDasharray="5,5"
                  opacity="0.6"
                />
              );
            })()}
            {renderedArrows.map((a, arrowIdx) => (
              <CanvasArrow
                key={a.id}
                arrow={a}
                arrowIdx={arrowIdx}
                allArrows={renderedArrows}
                boxes={arrowBoxes}
                boxesById={arrowBoxesById}
                isHovered={hoveredArrow === a.id}
                isConnectedToSelection={selectedBoxes.length > 0 && (selectedBoxes.includes(a.fromBox) || selectedBoxes.includes(a.toBox))}
                showNotes={showNotes}
                showCriticalPathCanvas={showCriticalPathCanvas}
                criticalPathArrowIds={canvasCriticalPathArrowIds}
                conflictArrowIds={canvasConflictArrowIds}
                isDraggingActive={dragging !== null}
                onAction={dispatchArrowAction}
              />
            ))}
            
            <CanvasOverlays
              selectionRect={selectionRect}
              snapLines={snapLines}
            />
          </svg>
          {topLevelBoxes.filter(box => showNotes || box.type !== 'callout').map(box => (
            <CanvasBox
              key={box.id}
              box={box}
              isSelected={selectedBoxes.includes(box.id)}
              isDragging={dragging === box.id}
              isEditingTitle={isEditingBoxTitle === box.id}
              editingDuration={editingDuration?.id === box.id ? editingDuration : null}
              viewAllFields={viewAllFields}
              departments={departments}
              resources={resources}
              isHovered={hoveredBox === box.id}
              zoom={zoom}
              showConnectHandles={hoveredBox === box.id || selectedBoxes.includes(box.id) || connecting !== null}
              isCritical={showCriticalPathCanvas && canvasCriticalPathIds.has(box.id)}
              isConflict={canvasConflictBoxIds.has(box.id)}
              isConnectSource={!!(clickConnectMode && clickConnectMode.boxId === box.id)}
              endText={getEnd(box.id)}
              startText={getStart(box.id)}
              showStartDates={showStartDates}
              showFinishDates={showFinishDates}
              subitemCount={subitemCountByBoxId.get(box.id) || 0}
              showSubitemBadge={showSubitemBadge}
              showUidBadge={showUidBadge}
              onAction={dispatchBoxAction}
            />
          ))}

          {/* Draft box for drag-to-create */}
          {draftBox && (
            <div
              className={`absolute rounded-lg shadow-lg cursor-pointer ${
                draftBox.type === 'callout' 
                  ? 'bg-white border-2 border-dashed border-yellow-500' 
                  : 'border-2 border-blue-500'
              }`}
              style={{ 
                left: draftBox.x, 
                top: draftBox.y, 
                width: draftBox.width, 
                height: draftBox.height, 
                zIndex: 100,
                opacity: 0.7,
                backgroundColor: draftBox.departmentColor || '#ffffff'
              }}
            >
              <div className="h-full flex items-center justify-center text-gray-400 text-sm">
                Click to place
              </div>
            </div>
          )}

          {/* Areas render in their own SVG, after every box, so they're always drawn
              on top of box content when enabled — a box's own z-index otherwise wins
              since it renders after the main arrows SVG. Only the dashed border/handles
              opt back into pointer events (see CanvasArea.tsx), same pattern as the main
              SVG above, so this doesn't block clicking boxes except right along an
              area's edge. */}
          {showAreas && (
            <svg
              className="absolute pointer-events-none"
              style={{
                zIndex: 15,
                left: '-5000px',
                top: '-5000px',
                width: '20000px',
                height: '20000px',
              }}
              viewBox="-5000 -5000 20000 20000"
            >
              {areas.map(area => (
                <CanvasArea
                  key={area.id}
                  area={area}
                  isComplete={areaCompletionById.get(area.id) === 100}
                  isSelected={selectedAreaId === area.id}
                  isEditing={editingAreaId === area.id}
                  editingTitle={editingAreaTitle}
                  isDragging={draggingArea?.id === area.id}
                  onTitleChange={setEditingAreaTitle}
                  onTitleCommit={() => { setAreas(prev => prev.map(a => a.id === area.id ? { ...a, title: editingAreaTitle } : a)); setEditingAreaId(null); }}
                  onStartDrag={(e) => {
                    if (e.button !== 0) return;
                    e.stopPropagation();
                    setSelectedAreaId(area.id);
                    setEditingAreaId(null);
                    const cr = canvasRef.current!.getBoundingClientRect();
                    setDraggingArea({ id: area.id, startMouseX: (e.clientX - cr.left - panOffset.x) / zoom, startMouseY: (e.clientY - cr.top - panOffset.y) / zoom, startAreaX: area.x, startAreaY: area.y });
                  }}
                  onStartResize={(handle, e) => {
                    if (e.button !== 0) return;
                    e.stopPropagation();
                    const cr = canvasRef.current!.getBoundingClientRect();
                    setResizingArea({ id: area.id, handle, startMouseX: (e.clientX - cr.left - panOffset.x) / zoom, startMouseY: (e.clientY - cr.top - panOffset.y) / zoom, startArea: { x: area.x, y: area.y, width: area.width, height: area.height } });
                  }}
                  onDoubleClickTitle={() => { setEditingAreaId(area.id); setEditingAreaTitle(area.title); setSelectedAreaId(area.id); }}
                  onSelectTitle={() => setSelectedAreaId(area.id)}
                  onDelete={() => { setAreas(prev => prev.filter(a => a.id !== area.id)); setSelectedAreaId(null); }}
                />
              ))}
            </svg>
          )}
        </div>

        {/* Minimap / radar */}
        <MinimapOverlay
          items={minimapItems}
          panOffset={panOffset}
          zoom={zoom}
          containerRef={canvasRef}
          onPanChange={setPanOffset}
        />
      </div>

      </div> {/* end sidebar+canvas wrapper */}

      <CanvasModals
        category={{
          showLegend, onCloseLegend: () => setShowLegend(false),
          departments, onDepartmentsChange: setDepartments,
        }}
        resourcesPanel={{
          showResources, onCloseResources: () => setShowResources(false),
          resources, onResourcesChange: setResources,
        }}
        historyPanel={{
          showHistory, onCloseHistory: () => setShowHistory(false),
          loadingHistory, historyVersions,
        }}
        title={title}
        shareDialog={{
          showShareDialog, onCloseShareDialog: () => setShowShareDialog(false),
          currentId, user,
        }}
        lastWriteToast={lastWriteToast}
        durationModeToast={durationModeToast}
        helpPanel={{
          showHelpPanel, onCloseHelpPanel: () => setShowHelpPanel(false),
          onStartTour: () => setShowHelp(true),
        }}
        tourOverlay={{
          showHelp,
          onCloseTour: () => {
            setShowHelp(false);
            if (user?.id) supabase.from("user_profiles").update({ tutorial_seen: true }).eq("id", user.id).then(() => {});
          },
        }}
        exportMenu={{
          showExportMenu, exportMenuY, onCloseExportMenu: () => setShowExportMenu(false),
          onExportAs: exportAs,
        }}
        linkPanel={{
          state: linkPanel, linkInputRef,
          onSaveLink: handleSaveLink, onDeleteLink: handleDeleteLink,
          onCloseLinkPanel: () => setLinkPanel(null),
        }}
        uidPanel={{
          state: uidPanel, uidInputRef,
          onSaveUid: handleSaveUid, onDeleteUid: handleDeleteUid,
          onCloseUidPanel: () => setUidPanel(null),
        }}
        boxes={boxes}
        canEditFlowchart={canEditFlowchart}
        contextMenu={{
          state: contextMenu, onCloseContextMenu: () => setContextMenu(null),
          selectedBoxes, clipboardHasItems: !!(clipboard && clipboard.boxes.length > 0),
          onContextMenuOpenStartConstraint: handleOpenStartConstraint,
          onOpenSubitems: (boxId: number) => setSubitemsPanelBoxId(boxId),
          onOpenUid: (boxId: number) => setUidPanel({ boxId }),
          onContextMenuToggle24_7: handleContextMenuToggle24_7,
          onContextMenuToggle24_5: handleContextMenuToggle24_5,
          onContextMenuToggleMilestone: handleContextMenuToggleMilestone,
          onContextMenuCopy: handleContextMenuCopy,
          onContextMenuDuplicate: handleContextMenuDuplicate,
          onContextMenuOpenResizeNote: handleContextMenuOpenResizeNote,
          onContextMenuTogglePrivate: handleContextMenuTogglePrivate,
          onContextMenuModifyLink: (boxId: number) => setLinkPanel({ boxId }),
          onContextMenuDeleteLink: handleDeleteLink,
          onContextMenuDelete: handleContextMenuDelete,
          onContextMenuAddBox: () => startDragToCreate("regular"),
          onContextMenuAddNote: () => startDragToCreate("callout"),
          onContextMenuPaste: pasteBoxes,
          onContextMenuSelectAll: () => setSelectedBoxes(topLevelBoxes.map(b => b.id)),
        }}
        startConstraintModal={{
          state: startConstraintModal, onChange: setStartConstraintModal,
          onSave: handleSaveStartConstraint,
          onCancel: () => setStartConstraintModal(null),
          durationUnit,
        }}
        arrowContextMenu={{
          state: arrowContextMenu, onCloseArrowContextMenu: () => setArrowContextMenu(null),
          onDeleteArrow: delArr,
        }}
        resizeNoteModal={{
          state: resizeNoteModal, onResizeNoteModalChange: setResizeNoteModal,
          onApplyResizeNote: handleApplyResizeNote,
          onCancelResizeNote: () => setResizeNoteModal(null),
        }}
        autosaveBanner={{
          pendingAutosave, onRestoreAutosave: restoreAutosave,
          onDismissAutosave: handleDismissAutosave,
        }}
        copyToFlowchartModal={{
          state: copyToFlowchartModal,
          onOpenCopyToFlowchart: (boxIds: number[]) => setCopyToFlowchartModal({ boxIds }),
          onCloseCopyToFlowchart: () => setCopyToFlowchartModal(null),
          onCopyToFlowchart: handleCopyToFlowchart,
        }}
        autoEndModal={{
          state: autoEndModal, onOpenAutoEnd: handleContextMenuOpenAutoEnd,
          onAutoEndChange: setAutoEndModal, onSaveAutoEnd: handleSaveAutoEnd,
          onCancelAutoEnd: () => setAutoEndModal(null),
        }}
      />

      {showPlanSettings && (
        <PlanSettingsDialog
          ganttStartDate={ganttStartDate}
          onGanttStartDateChange={(d, isoValue) => {
            setGanttStartDate(d);
            if (currentId) {
              lsSet(lsGanttStartDate(currentId), isoValue);
              updateFlowchartMeta(currentId, { gantt_start_date: isoValue });
            }
          }}
          fullDaySchedule={fullDaySchedule}
          onFullDayScheduleChange={(v) => {
            setFullDaySchedule(v);
            if (currentId) updateFlowchartMeta(currentId, { full_day_schedule: v });
          }}
          workDayStartHour={workDayStartHour}
          workDayEndHour={workDayEndHour}
          onWorkDayHoursChange={(start, end) => {
            setWorkDayStartHour(start);
            setWorkDayEndHour(end);
            if (currentId) {
              lsSet(lsWorkDayHours(currentId), JSON.stringify({ start, end }));
              updateFlowchartMeta(currentId, { work_day_start_hour: start, work_day_end_hour: end });
            }
          }}
          workDays={ganttWorkDays}
          onWorkDaysChange={(days) => {
            setGanttWorkDays(days);
            if (currentId) updateFlowchartMeta(currentId, { work_days: days });
          }}
          ganttNonWorkingDays={ganttNonWorkingDays}
          onNonWorkingDaysChange={(days) => {
            setGanttNonWorkingDays(days);
            if (currentId) updateFlowchartMeta(currentId, { non_working_days: days });
          }}
          showStartDates={showStartDates}
          onShowStartDatesChange={setShowStartDates}
          showFinishDates={showFinishDates}
          onShowFinishDatesChange={setShowFinishDates}
          onClose={() => setShowPlanSettings(false)}
        />
      )}

      {subitemsPanelBoxId !== null && (() => {
        const parentBox = boxes.find(b => b.id === subitemsPanelBoxId);
        if (!parentBox) return null;
        return (
          <SubitemsPanel
            parentBox={parentBox}
            subitems={subitemsForPanel}
            dependencyArrows={subitemArrowsForPanel}
            durationUnit={durationUnit}
            fullDaySchedule={fullDaySchedule}
            workHoursOnly={workHoursOnly}
            ganttWorkDays={ganttWorkDays}
            ganttNonWorkingDays={ganttNonWorkingDays}
            ganttStartDate={ganttStartDate}
            workDayStartHour={workDayStartHour}
            workDayHoursPerDay={workDayHoursPerDay}
            onAddSubitem={handleAddSubitem}
            onUpdateSubitem={handleUpdateSubitem}
            onDeleteSubitem={handleDeleteSubitem}
            onAddDependency={handleAddSubitemDependency}
            onRemoveDependency={handleRemoveSubitemDependency}
            onClose={() => setSubitemsPanelBoxId(null)}
          />
        );
      })()}

    </div>
  );
}
